/* MANAGED, DO NOT EDIT */

module.exports = [{
    "slug": "component/alert",
    "name": "Alert",
    "filename": "Alert.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\nimport Fade from \"../fade\";\n\n/**\n * The alert component can be used to display contextual user messages.\n */\nconst Alert = props => {\n  const {\n    className,\n    closeClassName,\n    closeAriaLabel,\n    tag: Tag,\n    theme,\n    open,\n    dismissible,\n    children,\n    transition,\n    fade,\n    ...attrs\n  } = props;\n\n  const classes = classNames(\n    className,\n    \"alert\",\n    `alert-${theme}`,\n    dismissible && \"alert-dismissible\"\n  );\n\n  const closeClasses = classNames(\"close\", closeClassName);\n\n  const alertTransition = {\n    ...Fade.defaultProps,\n    ...transition,\n    baseClass: fade ? transition.baseClass : \"\",\n    timeout: fade ? transition.timeout : 0\n  };\n\n  return (\n    <Fade\n      {...attrs}\n      {...alertTransition}\n      tag={Tag}\n      className={classes}\n      in={open}\n      role=\"alert\"\n    >\n      {dismissible ? (\n        <button\n          type=\"button\"\n          className={closeClasses}\n          aria-label={closeAriaLabel}\n          onClick={dismissible}\n        >\n          <span aria-hidden=\"true\">&times;</span>\n        </button>\n      ) : null}\n      {children}\n    </Fade>\n  );\n};\n\nAlert.propTypes = {\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The close button's class name.\n   */\n  closeClassName: PropTypes.string,\n  /**\n   * The close button's aria label.\n   */\n  closeAriaLabel: PropTypes.string,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * Whether it should fade, or not.\n   */\n  fade: PropTypes.bool,\n  /**\n   * Whether is open, or not.\n   */\n  open: PropTypes.bool,\n  /**\n   * Whether is dismissible, or not.\n   */\n  dismissible: PropTypes.func,\n  /**\n   * The transition config. See `Fade` component for more details.\n   */\n  transition: PropTypes.shape(Fade.propTypes),\n  /**\n   * The component tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nAlert.defaultProps = {\n  theme: \"primary\",\n  open: true,\n  tag: \"div\",\n  closeAriaLabel: \"Close\",\n  fade: true,\n  transition: {\n    ...Fade.defaultProps,\n    unmountOnExit: true\n  }\n};\n\nexport default Alert;\n",
    "examples": [{
        "name": "Alert",
        "filename": "01-basic-alert.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Alerts</h2>\n<p>Alerts come in various contextual theme colors.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Alert } from \"shards-react\";\n\n/**\n * ## Basic Alerts\n *\n * Alerts come in various contextual theme colors.\n */\nexport default function AlertExample() {\n  return (\n    <div className=\"example\">\n      <Alert theme=\"primary\">\n        Alert - Primary Theme (default) -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"secondary\">\n        Alert - Secondary Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"success\">\n        Alert - Success Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"danger\">\n        Alert - Danger Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"warning\">\n        Alert - Warning Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"info\">\n        Alert - Info Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"light\">\n        Alert - Light Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n      <Alert theme=\"dark\">\n        Alert - Dark Theme -{\" \"}\n        <a className=\"alert-link\" href=\"#\">\n          Example Link\n        </a>\n      </Alert>\n    </div>\n  );\n}\n"
    }, {
        "name": "Alert",
        "filename": "02-dismissible-alert.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Dismissible Alerts</h2>\n<p>Dismissible alerts allow you to hide them using an <code>X</code> button.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Alert } from \"shards-react\";\n\n/**\n * ## Dismissible Alerts\n *\n * Dismissible alerts allow you to hide them using an `X` button.\n */\nexport default class DismissibleAlertExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.dismiss = this.dismiss.bind(this);\n    this.state = { visible: true };\n  }\n\n  render() {\n    return (\n      <Alert dismissible={this.dismiss} open={this.state.visible}>\n        You can easily dismiss me using the <strong>close</strong> button &rarr;\n      </Alert>\n    );\n  }\n\n  dismiss() {\n    this.setState({ visible: false });\n  }\n}\n"
    }, {
        "name": "Alert",
        "filename": "03-self-dismissing-alert.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Self Dismissing Alert</h2>\n<p>Self-dismissible alerts can also be created using a few state tricks.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Alert, Button } from \"shards-react\";\n\n/**\n * ## Self Dismissing Alert\n *\n * Self-dismissible alerts can also be created using a few state tricks.\n */\nexport default class SelfDismissingAlertExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.interval = null;\n    this.state = {\n      visible: false,\n      countdown: 0,\n      timeUntilDismissed: 5\n    };\n\n    this.showAlert = this.showAlert.bind(this);\n    this.handleTimeChange = this.handleTimeChange.bind(this);\n    this.clearInterval = this.clearInterval.bind(this);\n  }\n\n  showAlert() {\n    this.clearInterval();\n    this.setState({ visible: true, countdown: 0, timeUntilDismissed: 5 });\n    this.interval = setInterval(this.handleTimeChange, 1000);\n  }\n\n  handleTimeChange() {\n    if (this.state.countdown < this.state.timeUntilDismissed - 1) {\n      this.setState({\n        ...this.state,\n        ...{ countdown: this.state.countdown + 1 }\n      });\n      return;\n    }\n\n    this.setState({ ...this.state, ...{ visible: false } });\n    this.clearInterval();\n  }\n\n  clearInterval() {\n    clearInterval(this.interval);\n    this.interval = null;\n  }\n\n  render() {\n    return (\n      <div>\n        <Alert className=\"mb-3\" open={this.state.visible} theme=\"success\">\n          Success! This alert will will be dismissed in{\" \"}\n          {this.state.timeUntilDismissed - this.state.countdown} seconds!\n        </Alert>\n        <Button onClick={this.showAlert}>Show Alert!</Button>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The alert component can be used to display contextual user messages.</p>\n"
    },
    "props": {
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "closeClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The close button's class name."
        },
        "closeAriaLabel": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The close button's aria label.",
            "defaultValue": {
                "value": "\"Close\"",
                "computed": false
            }
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color.",
            "defaultValue": {
                "value": "\"primary\"",
                "computed": false
            }
        },
        "fade": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should fade, or not.",
            "defaultValue": {
                "value": "true",
                "computed": false
            }
        },
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether is open, or not.",
            "defaultValue": {
                "value": "true",
                "computed": false
            }
        },
        "dismissible": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "Whether is dismissible, or not."
        },
        "transition": {
            "type": {
                "name": "shape",
                "value": "Fade.propTypes",
                "computed": true
            },
            "required": false,
            "description": "The transition config. See `Fade` component for more details.",
            "defaultValue": {
                "value": "{\n  ...Fade.defaultProps,\n  unmountOnExit: true\n}",
                "computed": false
            }
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/badge",
    "name": "Badge",
    "filename": "Badge.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Badges are really great for labels and count values.\n */\nconst Badge = props => {\n  let { tag: Tag, className, theme, pill, outline, ...attrs } = props;\n\n  const classes = classNames(\n    className,\n    \"badge\",\n    theme && !outline && `badge-${theme}`,\n    outline && `badge-outline-${theme}`,\n    pill && \"badge-pill\"\n  );\n\n  Tag = attrs.href && Tag === \"span\" ? \"a\" : Tag;\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nBadge.propTypes = {\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * Whether it should be outlined, or not.\n   */\n  outline: PropTypes.bool,\n  /**\n   * Whether it should be a pill, or not.\n   */\n  pill: PropTypes.bool,\n  /**\n   * The component tag.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nBadge.defaultProps = {\n  theme: \"primary\",\n  pill: false,\n  outline: false,\n  tag: \"span\"\n};\n\nexport default Badge;\n",
    "examples": [{
        "name": "Badge",
        "filename": "01-basic-badges.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Contextual Variations</h2>\n<p>The <code>theme</code> prop allows you to easily change the appearance of your badge using the main theme colors: <code>primary, secondary, success, danger, warning, info, light</code> and <code>dark</code>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Badge } from \"shards-react\";\n\n/**\n * ## Contextual Variations\n *\n * The `theme` prop allows you to easily change the appearance of your badge using the main theme colors: `primary, secondary, success, danger, warning, info, light` and `dark`.\n */\nexport default function ContextualBadgesExamples() {\n  return (\n    <div className=\"example\">\n      <Badge>Primary</Badge>\n      <Badge theme=\"secondary\">Secondary</Badge>\n      <Badge theme=\"success\">Success</Badge>\n      <Badge theme=\"info\">Info</Badge>\n      <Badge theme=\"warning\">Warning</Badge>\n      <Badge theme=\"danger\">Danger</Badge>\n      <Badge theme=\"light\">Light</Badge>\n      <Badge theme=\"dark\">Dark</Badge>\n    </div>\n  );\n}\n"
    }, {
        "name": "Badge",
        "filename": "02-pill-badges.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Pill-Shaped Badges</h2>\n<p>The pill <code>prop</code> applies a larger border radius that make your badges to look rounded.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Badge } from \"shards-react\";\n\n/**\n * ## Pill-Shaped Badges\n *\n * The pill `prop` applies a larger border radius that make your badges to look rounded.\n */\nexport default function PillBadgesExamples() {\n  return (\n    <div className=\"example\">\n      <Badge pill>Primary</Badge>\n      <Badge pill theme=\"secondary\">\n        Secondary\n      </Badge>\n      <Badge pill theme=\"success\">\n        Success\n      </Badge>\n      <Badge pill theme=\"info\">\n        Info\n      </Badge>\n      <Badge pill theme=\"warning\">\n        Warning\n      </Badge>\n      <Badge pill theme=\"danger\">\n        Danger\n      </Badge>\n      <Badge pill theme=\"light\">\n        Light\n      </Badge>\n      <Badge pill theme=\"dark\">\n        Dark\n      </Badge>\n    </div>\n  );\n}\n"
    }, {
        "name": "Badge",
        "filename": "03-outline-badges.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Outline Badges</h2>\n<p>The <code>outline</code> prop removes the background color and applies a thin border that make your badges to look outlined.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Badge } from \"shards-react\";\n\n/**\n * ## Outline Badges\n *\n * The `outline` prop removes the background color and applies a thin border that make your badges to look outlined.\n */\nexport default function OutlineBadgesExamples() {\n  return (\n    <div className=\"example\">\n      <Badge outline>Primary</Badge>\n      <Badge outline theme=\"secondary\">\n        Secondary\n      </Badge>\n      <Badge outline theme=\"success\">\n        Success\n      </Badge>\n      <Badge outline theme=\"info\">\n        Info\n      </Badge>\n      <Badge outline theme=\"warning\">\n        Warning\n      </Badge>\n      <Badge outline theme=\"danger\">\n        Danger\n      </Badge>\n      <Badge outline theme=\"light\">\n        Light\n      </Badge>\n      <Badge outline theme=\"dark\">\n        Dark\n      </Badge>\n    </div>\n  );\n}\n"
    }, {
        "name": "Badge",
        "filename": "04-mixed-badges.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Mixed Effects</h2>\n<p>You can also mix both the <code>pill</code> and <code>outline</code> props to get mixed results.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Badge } from \"shards-react\";\n\n/**\n * ## Mixed Effects\n *\n * You can also mix both the `pill` and `outline` props to get mixed results.\n */\nexport default function MixedBadgesExamples() {\n  return (\n    <div className=\"example\">\n      <Badge outline pill>\n        Primary\n      </Badge>\n      <Badge outline pill theme=\"secondary\">\n        Secondary\n      </Badge>\n      <Badge outline pill theme=\"success\">\n        Success\n      </Badge>\n      <Badge outline pill theme=\"info\">\n        Info\n      </Badge>\n      <Badge outline pill theme=\"warning\">\n        Warning\n      </Badge>\n      <Badge outline pill theme=\"danger\">\n        Danger\n      </Badge>\n      <Badge outline pill theme=\"light\">\n        Light\n      </Badge>\n      <Badge outline pill theme=\"dark\">\n        Dark\n      </Badge>\n    </div>\n  );\n}\n"
    }, {
        "name": "Badge",
        "filename": "05-link-badges.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Link Badges</h2>\n<p>Using the <code>href</code> prop you can turn your badges into regular links.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Badge } from \"shards-react\";\n\n/**\n * ## Link Badges\n *\n * Using the `href` prop you can turn your badges into regular links.\n */\nexport default function LinkBadgesExamples() {\n  return (\n    <div className=\"example\">\n      <Badge href=\"#\">Primary</Badge>\n      <Badge href=\"#\" theme=\"secondary\">\n        Secondary\n      </Badge>\n      <Badge href=\"#\" theme=\"success\">\n        Success\n      </Badge>\n      <Badge href=\"#\" theme=\"info\">\n        Info\n      </Badge>\n      <Badge href=\"#\" theme=\"warning\">\n        Warning\n      </Badge>\n      <Badge href=\"#\" theme=\"danger\">\n        Danger\n      </Badge>\n      <Badge href=\"#\" theme=\"light\">\n        Light\n      </Badge>\n      <Badge href=\"#\" theme=\"dark\">\n        Dark\n      </Badge>\n    </div>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Badges are really great for labels and count values.</p>\n"
    },
    "props": {
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color.",
            "defaultValue": {
                "value": "\"primary\"",
                "computed": false
            }
        },
        "outline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be outlined, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "pill": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be a pill, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component tag.",
            "defaultValue": {
                "value": "\"span\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/breadcrumb",
    "name": "Breadcrumb",
    "filename": "Breadcrumb.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * The breadcrumb component is great for indicating the current page's location within a navigational hierarchy.\n */\nconst Breadcrumb = props => {\n  const {\n    className,\n    listClassName,\n    children,\n    tag: Tag,\n    listTag: ListTag,\n    \"aria-label\": label,\n    ...attrs\n  } = props;\n\n  const classes = classNames(className);\n\n  const listClasses = classNames(\"breadcrumb\", listClassName);\n\n  return (\n    <Tag {...attrs} className={classes} aria-label={label}>\n      <ListTag className={listClasses}>{children}</ListTag>\n    </Tag>\n  );\n};\n\nBreadcrumb.propTypes = {\n  /**\n   * The breadcrumb list class name.\n   */\n  listClassName: PropTypes.string,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The aria label value.\n   */\n  \"aria-label\": PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The component tag name.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),\n  /**\n   * The breadcrumb list tag.\n   */\n  listTag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nBreadcrumb.defaultProps = {\n  \"aria-label\": \"breadcrumb\",\n  tag: \"nav\",\n  listTag: \"ol\"\n};\n\nexport default Breadcrumb;\n",
    "examples": [{
        "name": "Breadcrumb",
        "filename": "01-basic-breadcrumbs.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>Breadcrumbs can be composed using both the <code>BreadCrumb</code> and <code>BreadcrumbItem</code> components.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Breadcrumb, BreadcrumbItem } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * Breadcrumbs can be composed using both the `BreadCrumb` and `BreadcrumbItem` components.\n */\nexport default function BadgesExamples() {\n  return (\n    <Breadcrumb>\n      <BreadcrumbItem>\n        <a href=\"#\">Home</a>\n      </BreadcrumbItem>\n      <BreadcrumbItem active>Library</BreadcrumbItem>\n    </Breadcrumb>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "BreadcrumbItem",
        "methods": [],
        "props": {
            "active": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is active, or not."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag.",
                "defaultValue": {
                    "value": "\"li\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The breadcrumb component is great for indicating the current page's location within a navigational hierarchy.</p>\n"
    },
    "props": {
        "listClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The breadcrumb list class name."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "aria-label": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The aria label value.",
            "defaultValue": {
                "value": "\"breadcrumb\"",
                "computed": false
            }
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component tag name.",
            "defaultValue": {
                "value": "\"nav\"",
                "computed": false
            }
        },
        "listTag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The breadcrumb list tag.",
            "defaultValue": {
                "value": "\"ol\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/button",
    "name": "Button",
    "filename": "Button.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Buttons are Bootstrap's core component for triggering various actions. In Shards, they're very flxible, support multiple sizes, styles, states and many more.\n */\nclass Button extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.onClick = this.onClick.bind(this);\n  }\n\n  onClick(e) {\n    if (this.props.disabled) {\n      e.preventDefault();\n      return;\n    }\n\n    if (this.props.onClick) {\n      this.props.onClick(e);\n    }\n  }\n\n  render() {\n    let {\n      className,\n      theme,\n      size,\n      pill,\n      outline,\n      squared,\n      active,\n      disabled,\n      innerRef,\n      tag: Tag,\n      block,\n      ...attrs\n    } = this.props;\n\n    const classes = classNames(\n      className,\n      \"btn\",\n      theme && `btn-${outline ? \"outline-\" : \"\"}${theme}`,\n      size && `btn-${size}`,\n      pill && \"btn-pill\",\n      squared && \"btn-squared\",\n      block && \"btn-block\",\n      active && \"active\"\n    );\n\n    Tag = attrs.href && Tag === \"button\" ? \"a\" : Tag;\n    const tagType = Tag === \"button\" && attrs.onClick ? \"button\" : undefined;\n\n    return (\n      <Tag\n        ref={innerRef}\n        type={tagType}\n        {...attrs}\n        className={classes}\n        disabled={disabled}\n        onClick={this.onClick}\n      />\n    );\n  }\n}\n\nButton.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it is outline, or not.\n   */\n  outline: PropTypes.bool,\n  /**\n   * Whether it is pill, or not.\n   */\n  pill: PropTypes.bool,\n  /**\n   * Whether it is squared, or not.\n   */\n  squared: PropTypes.bool,\n  /**\n   * Whether it is active, or not.\n   */\n  active: PropTypes.bool,\n  /**\n   * Whether it should be displayed as a block (full-width), or not.\n   */\n  block: PropTypes.bool,\n  /**\n   * Whether it is disabled, or not.\n   */\n  disabled: PropTypes.bool,\n  /**\n   * The component tag.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),\n  /**\n   * The inner ref.\n   * @type {[type]}\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nButton.defaultProps = {\n  theme: \"primary\",\n  tag: \"button\"\n};\n\nexport default Button;\n",
    "examples": [{
        "name": "Button",
        "filename": "01-basic-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Button Themes</h2>\n<p>Using the theme prop you can easily change the appearance of your button using one the main theme colors: <code>primary, secondary, success, danger, warning, info, light</code> and <code>dark</code>. The default theme value is <code>primary</code>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Button Themes\n *\n * Using the theme prop you can easily change the appearance of your button using one the main theme colors: `primary, secondary, success, danger, warning, info, light` and `dark`. The default theme value is `primary`.\n */\nexport default function ButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button>Primary</Button>\n      <Button theme=\"secondary\">Secondary</Button>\n      <Button theme=\"success\">Success</Button>\n      <Button theme=\"info\">Info</Button>\n      <Button theme=\"warning\">Warning</Button>\n      <Button theme=\"danger\">Danger</Button>\n      <Button theme=\"light\">Light</Button>\n      <Button theme=\"dark\">Dark</Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "02-outline-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Outline Styled Buttons</h2>\n<p>You can use the <code>outline</code> prop to remove the background color and apply a thin border that make your buttons look outlined.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Outline Styled Buttons\n *\n * You can use the `outline` prop to remove the background color and apply a thin border that make your buttons look outlined.\n */\nexport default function OutlineButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button outline>Primary</Button>\n      <Button outline theme=\"secondary\">\n        Secondary\n      </Button>\n      <Button outline theme=\"success\">\n        Success\n      </Button>\n      <Button outline theme=\"info\">\n        Info\n      </Button>\n      <Button outline theme=\"warning\">\n        Warning\n      </Button>\n      <Button outline theme=\"danger\">\n        Danger\n      </Button>\n      <Button outline theme=\"light\">\n        Light\n      </Button>\n      <Button outline theme=\"dark\">\n        Dark\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "03-pill-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Pill Shaped Buttons</h2>\n<p>The <code>pill</code> prop applies a larger border radius that make your buttons look more rounded and pill-like.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Pill Shaped Buttons\n *\n * The `pill` prop applies a larger border radius that make your buttons look more rounded and pill-like.\n */\nexport default function PillButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button pill>Primary</Button>\n      <Button pill theme=\"secondary\">\n        Secondary\n      </Button>\n      <Button pill theme=\"success\">\n        Success\n      </Button>\n      <Button pill theme=\"info\">\n        Info\n      </Button>\n      <Button pill theme=\"warning\">\n        Warning\n      </Button>\n      <Button pill theme=\"danger\">\n        Danger\n      </Button>\n      <Button pill theme=\"light\">\n        Light\n      </Button>\n      <Button pill theme=\"dark\">\n        Dark\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "04-mixed-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Mixed Styles</h2>\n<p>Similarly to Badges you can also mix both the <code>pill</code> and <code>outline</code> props to get a mixed \"outline-pill\" result.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Mixed Styles\n *\n * Similarly to Badges you can also mix both the `pill` and `outline` props to get a mixed \"outline-pill\" result.\n */\nexport default function OutlinePillButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button outline pill>\n        Primary\n      </Button>\n      <Button outline pill theme=\"secondary\">\n        Secondary\n      </Button>\n      <Button outline pill theme=\"success\">\n        Success\n      </Button>\n      <Button outline pill theme=\"info\">\n        Info\n      </Button>\n      <Button outline pill theme=\"warning\">\n        Warning\n      </Button>\n      <Button outline pill theme=\"danger\">\n        Danger\n      </Button>\n      <Button outline pill theme=\"light\">\n        Light\n      </Button>\n      <Button outline pill theme=\"dark\">\n        Dark\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "05-squared-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Squared Style</h2>\n<p>Using the <code>squared</code> prop you can style your buttons to look, well, squared.</p>\n<blockquote>\n<p><strong>Note</strong>: The <code>pill</code> prop has priority over the <code>squared</code> prop.</p>\n</blockquote>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Squared Style\n *\n * Using the `squared` prop you can style your buttons to look, well, squared.\n *\n * > **Note**: The `pill` prop has priority over the `squared` prop.\n */\nexport default function SquaredButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button squared>Primary</Button>\n      <Button squared theme=\"secondary\">\n        Secondary\n      </Button>\n      <Button squared theme=\"success\">\n        Success\n      </Button>\n      <Button squared theme=\"info\">\n        Info\n      </Button>\n      <Button squared theme=\"warning\">\n        Warning\n      </Button>\n      <Button squared theme=\"danger\">\n        Danger\n      </Button>\n      <Button squared theme=\"light\">\n        Light\n      </Button>\n      <Button squared theme=\"dark\">\n        Dark\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "06-outline-squared-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Mixed Outline-Squared Style</h2>\n<p>Mixing the <code>outline</code> and <code>squared</code> prop is also possible and it will render an outlined and squared button.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Mixed Outline-Squared Style\n *\n * Mixing the `outline` and `squared` prop is also possible and it will render an outlined and squared button.\n */\nexport default function OutlineSquaredPillButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button outline squared>\n        Primary\n      </Button>\n      <Button outline squared theme=\"secondary\">\n        Secondary\n      </Button>\n      <Button outline squared theme=\"success\">\n        Success\n      </Button>\n      <Button outline squared theme=\"info\">\n        Info\n      </Button>\n      <Button outline squared theme=\"warning\">\n        Warning\n      </Button>\n      <Button outline squared theme=\"danger\">\n        Danger\n      </Button>\n      <Button outline squared theme=\"light\">\n        Light\n      </Button>\n      <Button outline squared theme=\"dark\">\n        Dark\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "07-sizes-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Button Sizes</h2>\n<p>Using the <code>size</code> prop you can control the size of your buttons. There are three sizes available: normal (default), <code>lg</code> for large buttons and <code>sm</code> for small buttons.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Button Sizes\n *\n * Using the `size` prop you can control the size of your buttons. There are three sizes available: normal (default), `lg` for large buttons and `sm` for small buttons.\n */\nexport default function ButtonsSizesExample() {\n  return (\n    <div className=\"example\">\n      <Button size=\"lg\">Large</Button>\n      <Button>Normal</Button>\n      <Button size=\"sm\">Small</Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "08-active-state-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Active State</h2>\n<p>Controlling the active state and appearance of your buttons can be achieved via the <code>active</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Active State\n *\n * Controlling the active state and appearance of your buttons can be achieved via the `active` prop.\n */\nexport default function ActiveButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button active theme=\"success\">\n        Success Active\n      </Button>\n      <Button active theme=\"danger\">\n        Danger Active\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "09-disabled-state-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Disabled State</h2>\n<p>Similarly to the active state, the disabled state can also be controlled via the <code>disabled</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Disabled State\n *\n * Similarly to the active state, the disabled state can also be controlled via the `disabled` prop.\n */\nexport default function DisabledButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button disabled theme=\"success\">\n        Success Disabled\n      </Button>\n      <Button disabled theme=\"danger\">\n        Danger Disabled\n      </Button>\n    </div>\n  );\n}\n"
    }, {
        "name": "Button",
        "filename": "10-block-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Block Level Buttons</h2>\n<p>Using the <code>block</code> prop you can make buttons display using the full-width of their parent element.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button } from \"shards-react\";\n\n/**\n * ## Block Level Buttons\n *\n * Using the `block` prop you can make buttons display using the full-width of their parent element.\n */\nexport default function BlockButtonsExample() {\n  return (\n    <div className=\"example\">\n      <Button block>Primary</Button>\n      <Button block theme=\"secondary\">\n        Secondary\n      </Button>\n    </div>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Buttons are Bootstrap's core component for triggering various actions. In Shards, they're very flxible, support multiple sizes, styles, states and many more.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color.",
            "defaultValue": {
                "value": "\"primary\"",
                "computed": false
            }
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "outline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is outline, or not."
        },
        "pill": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is pill, or not."
        },
        "squared": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is squared, or not."
        },
        "active": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is active, or not."
        },
        "block": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be displayed as a block (full-width), or not."
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is disabled, or not."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component tag.",
            "defaultValue": {
                "value": "\"button\"",
                "computed": false
            }
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref.\n@type {[type]}"
        }
    }
}, {
    "slug": "component/button-group",
    "name": "ButtonGroup",
    "filename": "ButtonGroup.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Button groups allow you to group buttons together on a single line.\n */\nconst ButtonGroup = props => {\n  const { className, vertical, size, ...attrs } = props;\n\n  const classes = classNames(\n    className,\n    size && `btn-group-${size}`,\n    vertical ? \"btn-group-vertical\" : \"btn-group\"\n  );\n\n  return <div className={classes} {...attrs} />;\n};\n\nButtonGroup.propTypes = {\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it is vertical, or not.\n   */\n  vertical: PropTypes.bool\n};\n\nexport default ButtonGroup;\n",
    "examples": [{
        "name": "ButtonGroup",
        "filename": "1-basic-button-groups.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>You can create button groups using the <code>ButtonGroup</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, ButtonGroup } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * You can create button groups using the `ButtonGroup` component.\n */\nexport default function ButtonGroupExample() {\n  return (\n    <ButtonGroup>\n      <Button>Left</Button>\n      <Button>Middle</Button>\n      <Button>Right</Button>\n    </ButtonGroup>\n  );\n}\n"
    }, {
        "name": "ButtonGroup",
        "filename": "2-size-button-groups.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Button Group Size</h2>\n<p>The button group's size can be controlled via the <code>size</code> prop. The button group can be normal (default), <code>small</code> or <code>large</code>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, ButtonGroup } from \"shards-react\";\n\n/**\n * ## Button Group Size\n *\n * The button group's size can be controlled via the `size` prop. The button group can be normal (default), `small` or `large`.\n */\nexport default function SizeButtonGroupExample() {\n  return (\n    <div className=\"example\">\n      <ButtonGroup size=\"lg\" className=\"mr-2\">\n        <Button>Large</Button>\n        <Button>Large</Button>\n      </ButtonGroup>\n\n      <ButtonGroup className=\"mr-2\">\n        <Button>Normal</Button>\n        <Button>Normal</Button>\n      </ButtonGroup>\n\n      <ButtonGroup size=\"sm\" className=\"mr-2\">\n        <Button>Small</Button>\n        <Button>Small</Button>\n      </ButtonGroup>\n    </div>\n  );\n}\n"
    }, {
        "name": "ButtonGroup",
        "filename": "3-vertical-button-groups.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Vertical Button Groups</h2>\n<p>You can stack button groups vertically using the <code>vertical</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, ButtonGroup } from \"shards-react\";\n\n/**\n * ## Vertical Button Groups\n *\n * You can stack button groups vertically using the `vertical` prop.\n */\nexport default function VerticalButtonGroupExample() {\n  return (\n    <ButtonGroup vertical>\n      <Button>Button 1</Button>\n      <Button>Button 2</Button>\n    </ButtonGroup>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Button groups allow you to group buttons together on a single line.</p>\n"
    },
    "props": {
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "vertical": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is vertical, or not."
        }
    }
}, {
    "slug": "component/button-toolbar",
    "name": "ButtonToolbar",
    "filename": "ButtonToolbar.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Button toolbars allow you to group a series of button or input groups on a single line.\n */\nconst ButtonToolbar = props => {\n  const { className, ...attrs } = props;\n  const classes = classNames(className, \"btn-toolbar\");\n\n  return <div className={classes} {...attrs} />;\n};\n\nButtonToolbar.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.node),\n    PropTypes.node\n  ])\n};\n\nexport default ButtonToolbar;\n",
    "examples": [{
        "name": "ButtonToolbar",
        "filename": "01-basic-button-toolbar.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "code": "import React from \"react\";\nimport {\n  Button,\n  ButtonGroup,\n  ButtonToolbar,\n  FormInput,\n  InputGroup\n} from \"shards-react\";\n\nexport default function ButtonToolbarExample() {\n  return (\n    <ButtonToolbar>\n      <ButtonGroup size=\"sm\">\n        <Button>Create</Button>\n        <Button>Edit</Button>\n      </ButtonGroup>\n\n      <InputGroup size=\"sm\" className=\"ml-auto\">\n        <FormInput placeholder=\"Search...\" />\n      </InputGroup>\n    </ButtonToolbar>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Button toolbars allow you to group a series of button or input groups on a single line.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "node"
                    }
                }, {
                    "name": "node"
                }]
            },
            "required": false,
            "description": "The children nodes."
        }
    }
}, {
    "slug": "component/card",
    "name": "Card",
    "filename": "Card.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Cards provide a flexible content container that you can use to display a variety of content using contextual background colors, headers and footers.\n */\nconst Card = props => {\n  const {\n    className,\n    innerRef,\n    tag: Tag,\n    theme,\n    outline,\n    small,\n    ...attrs\n  } = props;\n\n  const classes = classNames(\n    className,\n    \"card\",\n    small && \"card-small\",\n    theme && `${outline ? \"border\" : \"bg\"}-${theme}`\n  );\n\n  return <Tag {...attrs} className={classes} ref={innerRef} />;\n};\n\nCard.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * Whether it is outline, or not.\n   */\n  outline: PropTypes.bool,\n  /**\n   * The component tag.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),\n  /**\n   * Whether the card is small, or not.\n   */\n  small: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.string,\n    PropTypes.object,\n    PropTypes.func\n  ])\n};\n\nCard.defaultProps = {\n  tag: \"div\"\n};\n\nexport default Card;\n",
    "examples": [{
        "name": "Card",
        "filename": "01-basic-card.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>By default, cards fill in the full width of their parent element, however this can be customized via styling. Cards also support a large variety of content including images, links, text and more. Make sure to check out all the examples below.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Card,\n  CardHeader,\n  CardTitle,\n  CardImg,\n  CardBody,\n  CardFooter,\n  Button\n} from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * By default, cards fill in the full width of their parent element, however this can be customized via styling. Cards also support a large variety of content including images, links, text and more. Make sure to check out all the examples below.\n */\nexport default function BasicCardExample() {\n  return (\n    <Card style={{ maxWidth: \"300px\" }}>\n      <CardHeader>Card header</CardHeader>\n      <CardImg src=\"https://place-hold.it/300x200\" />\n      <CardBody>\n        <CardTitle>Lorem Ipsum</CardTitle>\n        <p>Lorem ipsum dolor sit amet.</p>\n        <Button>Read more &rarr;</Button>\n      </CardBody>\n      <CardFooter>Card footer</CardFooter>\n    </Card>\n  );\n}\n"
    }, {
        "name": "Card",
        "filename": "02-card-body.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Card Body</h2>\n<p>The core building block of a card is the <code>CardBody</code>. You can use it whenever you'd like to add a padded section within a card.</p>\n"
        },
        "code": "import React from 'react'\nimport { Card, CardBody } from 'shards-react'\n\n/**\n * ## Card Body\n *\n * The core building block of a card is the `CardBody`. You can use it whenever you'd like to add a padded section within a card.\n */\nexport default function CardBodyExample() {\n  return (\n    <Card>\n      <CardBody>\n        Nunc quis nisl ac justo elementum sagittis in quis justo.\n      </CardBody>\n    </Card>\n  )\n}\n"
    }, {
        "name": "Card",
        "filename": "03-card-body-title-subtitle.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Card Body Title and Subtitle</h2>\n<p>You can display a title and subtitle for your card using the <code>CardTitle</code> and <code>CardSubtitle</code> components.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Card, CardBody, CardTitle, CardSubtitle } from \"shards-react\";\n\n/**\n * ## Card Body Title and Subtitle\n *\n * You can display a title and subtitle for your card using the `CardTitle` and `CardSubtitle` components.\n */\nexport default function CardBodyTitleSubtitleExample() {\n  return (\n    <Card>\n      <CardBody>\n        <CardTitle>Card Title</CardTitle>\n        <CardSubtitle>Card subtitle</CardSubtitle>\n        Nunc quis nisl ac justo elementum sagittis in quis justo.\n      </CardBody>\n    </Card>\n  );\n}\n"
    }, {
        "name": "Card",
        "filename": "04-card-image.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Card Image</h2>\n<p>Using the <code>CardImg</code> component you can place a responsive image either on top of bottom of the card using the <code>top</code> or <code>bottom</code> props.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Container, Row, Col, Card, CardBody, CardImg } from \"shards-react\";\n\n/**\n * ## Card Image\n *\n * Using the `CardImg` component you can place a responsive image either on top of bottom of the card using the `top` or `bottom` props.\n */\nexport default function CardBodyExample() {\n  return (\n    <Container>\n      <Row>\n        <Col>\n          <Card>\n            <CardImg top src=\"https://place-hold.it/300x200\" />\n            <CardBody>\n              <p>This is the body of the card.</p>\n            </CardBody>\n          </Card>\n        </Col>\n\n        <Col>\n          <Card>\n            <CardBody>\n              <p>This is the body of the card.</p>\n            </CardBody>\n            <CardImg bottom src=\"https://place-hold.it/300x200\" />\n          </Card>\n        </Col>\n      </Row>\n    </Container>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardBody",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "children": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "arrayOf",
                        "value": {
                            "name": "node"
                        }
                    }, {
                        "name": "node"
                    }]
                },
                "required": false,
                "description": "The children nodes."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardColumns",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardDeck",
        "methods": [],
        "props": {
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardFooter",
        "methods": [],
        "props": {
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardGroup",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardHeader",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardImg",
        "methods": [],
        "props": {
            "top": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether the image is positioned at the top of the card, or not."
            },
            "bottom": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether the image is positioned at the bottom of the card, or not."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"img\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardImgOverlay",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardLink",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"a\"",
                    "computed": false
                }
            },
            "innerRef": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "object"
                    }, {
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The inner ref."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardSubtitle",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"h6\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardText",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The tag type.",
                "defaultValue": {
                    "value": "\"p\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "CardTitle",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"h5\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Cards provide a flexible content container that you can use to display a variety of content using contextual background colors, headers and footers.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color."
        },
        "outline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is outline, or not."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component tag.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        },
        "small": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the card is small, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "object"
                }, {
                    "name": "func"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/collapse",
    "name": "Collapse",
    "filename": "Collapse.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport Transition from \"react-transition-group/Transition\";\nimport pick from \"lodash.pick\";\nimport omit from \"lodash.omit\";\n\nimport { TIMEOUT, TRANSITION_KEYS, TRANSITION_CLASS_MAP } from \"../constants\";\nimport { reflow, getNodeHeight } from \"../utils\";\n\n/**\n * The `Collapse` component allows you to easily toggle the visibility of your content.\n */\nclass Collapse extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      height: null\n    };\n  }\n\n  render() {\n    const {\n      tag: Tag,\n      open,\n      className,\n      navbar,\n      children,\n      innerRef,\n      ...attrs\n    } = this.props;\n\n    const { height } = this.state;\n    const transitionProps = pick(attrs, TRANSITION_KEYS);\n    const childProps = omit(attrs, TRANSITION_KEYS);\n\n    return (\n      <Transition\n        {...transitionProps}\n        in={open}\n        onEntering={this.onEntering.bind(this)}\n        onEntered={this.onEntered.bind(this)}\n        onExit={this.onExit.bind(this)}\n        onExiting={this.onExiting.bind(this)}\n        onExited={this.onExited.bind(this)}\n      >\n        {status => {\n          const style = {\n            height: height || null,\n            display: status !== \"exited\" && \"block\"\n          };\n\n          const classes = classNames(\n            className,\n            TRANSITION_CLASS_MAP[status] || \"collapse\",\n            navbar && \"navbar-collapse\"\n          );\n\n          return (\n            <Tag\n              {...childProps}\n              style={{ ...childProps.style, ...style }}\n              className={classes}\n              ref={innerRef}\n            >\n              {children}\n            </Tag>\n          );\n        }}\n      </Transition>\n    );\n  }\n\n  onEntering(node, isAppearing) {\n    this.setState({ height: getNodeHeight(node) });\n    this.props.onEntering(node, isAppearing);\n  }\n\n  onEntered(node, isAppearing) {\n    this.setState({ height: null });\n    this.props.onEntered(node, isAppearing);\n  }\n\n  onExit(node) {\n    this.setState({ height: getNodeHeight(node) });\n    this.props.onExit(node);\n  }\n\n  onExiting(node) {\n    reflow(node);\n    this.setState({ height: 0 });\n    this.props.onExiting(node);\n  }\n\n  onExited(node) {\n    this.setState({ height: null });\n    this.props.onExited(node);\n  }\n}\n\nCollapse.propTypes = {\n  ...Transition.propTypes,\n  /**\n   * Whether it is open, or not.\n   */\n  open: PropTypes.bool,\n  /**\n   * The children components.\n   */\n  children: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.node),\n    PropTypes.node\n  ]),\n  /**\n   * The element tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),\n  /**\n   * The class name.\n   */\n  className: PropTypes.node,\n  /**\n   * Whether it is located inside a navbar, or not.\n   */\n  navbar: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.func,\n    PropTypes.string,\n    PropTypes.object\n  ])\n};\n\nCollapse.defaultProps = {\n  ...Transition.defaultProps,\n  open: false,\n  appear: false,\n  enter: true,\n  exit: true,\n  tag: \"div\",\n  timeout: TIMEOUT.COLLAPSE\n};\n\nexport default Collapse;\n",
    "examples": [{
        "name": "Collapse",
        "filename": "01-basic-collapse.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n"
        },
        "code": "import React from \"react\";\nimport Button from \"shards-react/button\";\nimport Collapse from \"shards-react/collapse\";\n\n/**\n * ## Basic Example\n */\nexport default class BasicCollapseExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { collapse: false };\n  }\n\n  toggle() {\n    this.setState({ collapse: !this.state.collapse });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button onClick={this.toggle}>Toggle</Button>\n        <Collapse open={this.state.collapse}>\n          <div className=\"p-3 mt-3 border rounded\">\n            <h5>😍 Now you see me!</h5>\n            <span>\n              In sagittis nibh non arcu viverra, nec imperdiet quam suscipit.\n              Sed porta eleifend scelerisque. Vestibulum dapibus quis arcu a\n              facilisis.\n            </span>\n          </div>\n        </Collapse>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Collapse",
        "filename": "02-initial-visibility.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Initial Visibility</h2>\n<p>You can control the visibility of your collapsed element via the <code>open</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport Button from \"shards-react/button\";\nimport Collapse from \"shards-react/collapse\";\n\n/**\n * ## Initial Visibility\n *\n * You can control the visibility of your collapsed element via the `open` prop.\n */\nexport default class CollapseInitialVisibilityExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { collapse: true };\n  }\n\n  toggle() {\n    this.setState({ collapse: !this.state.collapse });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button onClick={this.toggle}>Toggle</Button>\n        <Collapse open={this.state.collapse}>\n          <div className=\"p-3 mt-3 border rounded\">\n            <h5>😁 I am already visible!</h5>\n            <span>\n              In sagittis nibh non arcu viverra, nec imperdiet quam suscipit.\n              Sed porta eleifend scelerisque. Vestibulum dapibus quis arcu a\n              facilisis.\n            </span>\n          </div>\n        </Collapse>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>Collapse</code> component allows you to easily toggle the visibility of your content.</p>\n"
    },
    "props": {
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is open, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "children": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "node"
                    }
                }, {
                    "name": "node"
                }]
            },
            "required": false,
            "description": "The children components."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The element tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        },
        "className": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The class name."
        },
        "navbar": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is located inside a navbar, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }, {
                    "name": "object"
                }]
            },
            "required": false,
            "description": "The inner ref."
        },
        "appear": {
            "defaultValue": {
                "value": "false",
                "computed": false
            },
            "required": false
        },
        "enter": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        },
        "exit": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        },
        "timeout": {
            "defaultValue": {
                "value": "TIMEOUT.COLLAPSE",
                "computed": true
            },
            "required": false
        }
    }
}, {
    "slug": "component/container",
    "name": "Container",
    "filename": "Container.js",
    "code": "import PropTypes from \"prop-types\";\nimport React from \"react\";\nimport classNames from \"classnames\";\n\n/**\n * Shards React provides support for all native Bootstrap 4 layout elements including **containers**, **rows**, **columns** and **form rows** so you can use its full power while building your project's responsive layout powered by flexbox.\n */\nconst Container = props => {\n  const { className, fluid, tag: Tag, ...attrs } = props;\n  const classes = classNames(\n    className,\n    fluid ? \"container-fluid\" : \"container\"\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nContainer.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * Whether it is fluid, or not.\n   */\n  fluid: PropTypes.bool,\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nContainer.defaultProps = {\n  tag: \"div\"\n};\n\nexport default Container;\n",
    "examples": [{
        "name": "Container",
        "filename": "01-basic-layout.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n"
        },
        "code": "import React from \"react\";\nimport { Container, Row, Col } from \"shards-react\";\n\n/**\n * ## Basic Example\n */\nexport default class Example extends React.Component {\n  render() {\n    return (\n      <Container className=\"dr-example-container\">\n        <Row>\n          <Col>1 / 12</Col>\n          <Col>2 / 12</Col>\n          <Col>3 / 12</Col>\n          <Col>4 / 12</Col>\n          <Col>5 / 12</Col>\n          <Col>6 / 12</Col>\n          <Col>7 / 12</Col>\n          <Col>8 / 12</Col>\n          <Col>9 / 12</Col>\n          <Col>10 / 12</Col>\n          <Col>11 / 12</Col>\n          <Col>12 / 12</Col>\n        </Row>\n\n        <Row>\n          <Col>1 / 6</Col>\n          <Col>2 / 6</Col>\n          <Col>3 / 6</Col>\n          <Col>4 / 6</Col>\n          <Col>5 / 6</Col>\n          <Col>6 / 6</Col>\n        </Row>\n\n        <Row>\n          <Col>1 / 3</Col>\n          <Col>2 / 3</Col>\n          <Col>3 / 3</Col>\n        </Row>\n\n        <Row>\n          <Col>1 / 2</Col>\n          <Col>2 / 2</Col>\n        </Row>\n\n        <Row>\n          <Col>1 / 1</Col>\n        </Row>\n      </Container>\n    );\n  }\n}\n"
    }, {
        "name": "Container",
        "filename": "02-layout-explained.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Containers</h2>\n<p>Containers are the most fundamental and required layout element for your application or website's layout. You can use the <code>Container</code> component for a fixed container, and apply the <code>fluid</code> prop for a fluid container.</p>\n<h2>Rows</h2>\n<p>The <code>Row</code> component must be placed inside a <code>Container</code> component and it's  used to define a row of columns. You can also apply the <code>form</code> prop onto a <code>Row</code> component to turn it into a <code>form-row</code> better suited for building form layouts that help you create a layout with more compact margins.</p>\n<h2>Columns</h2>\n<p>The <code>Col</code> component is used to represent a column and must be placed inside a <code>Row</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Container, Row, Col } from \"shards-react\";\n\n/**\n * ## Containers\n *\n * Containers are the most fundamental and required layout element for your application or website's layout. You can use the `Container` component for a fixed container, and apply the `fluid` prop for a fluid container.\n *\n * ## Rows\n *\n * The `Row` component must be placed inside a `Container` component and it's  used to define a row of columns. You can also apply the `form` prop onto a `Row` component to turn it into a `form-row` better suited for building form layouts that help you create a layout with more compact margins.\n *\n * ## Columns\n *\n * The `Col` component is used to represent a column and must be placed inside a `Row` component.\n */\nexport default class Example extends React.Component {\n  render() {\n    return (\n      <Container className=\"dr-example-container\">\n        <Row>\n          <Col sm=\"12\" lg=\"6\">\n            sm=12 lg=6\n          </Col>\n          <Col sm=\"12\" lg=\"6\">\n            sm=12 lg=6\n          </Col>\n        </Row>\n\n        <Row>\n          <Col sm=\"12\" md=\"4\" lg=\"3\">\n            sm=12 md=4 lg=3\n          </Col>\n          <Col sm=\"12\" md=\"4\" lg=\"6\">\n            sm=12 md=4 lg=6\n          </Col>\n          <Col sm=\"12\" md=\"4\" lg=\"3\">\n            sm=12 md=4 lg=3\n          </Col>\n        </Row>\n\n        <Row>\n          <Col sm={{ size: 8, order: 2, offset: 2 }}>\n            .col-sm-8 .order-sm-2 .offset-sm-2\n          </Col>\n        </Row>\n      </Container>\n    );\n  }\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "Col",
        "methods": [],
        "props": {
            "xs": {
                "type": {
                    "name": "custom",
                    "raw": "CustomPropTypes.column"
                },
                "required": false,
                "description": "Col number or config object for xs viewports."
            },
            "sm": {
                "type": {
                    "name": "custom",
                    "raw": "CustomPropTypes.column"
                },
                "required": false,
                "description": "Col number or config object for sm viewports."
            },
            "md": {
                "type": {
                    "name": "custom",
                    "raw": "CustomPropTypes.column"
                },
                "required": false,
                "description": "Col number or config object for md viewports."
            },
            "lg": {
                "type": {
                    "name": "custom",
                    "raw": "CustomPropTypes.column"
                },
                "required": false,
                "description": "Col number or config object for lg viewports."
            },
            "xl": {
                "type": {
                    "name": "custom",
                    "raw": "CustomPropTypes.column"
                },
                "required": false,
                "description": "Col number or config object for xl viewports."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "breakpoints": {
                "type": {
                    "name": "array"
                },
                "required": false,
                "description": "The available breakpoints.",
                "defaultValue": {
                    "value": "BREAKPOINTS",
                    "computed": true
                }
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "Row",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "noGutters": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it has gutters, or not."
            },
            "form": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is located inside a form, or not."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "'div'",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Shards React provides support for all native Bootstrap 4 layout elements including <strong>containers</strong>, <strong>rows</strong>, <strong>columns</strong> and <strong>form rows</strong> so you can use its full power while building your project's responsive layout powered by flexbox.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "fluid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is fluid, or not."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/dropdown",
    "name": "Dropdown",
    "filename": "Dropdown.js",
    "code": "import React from \"react\";\nimport ReactDOM from \"react-dom\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport { Manager } from \"react-popper\";\nimport omit from \"lodash.omit\";\n\nimport { DropdownContext } from \"./DropdownContext\";\nimport { KEYCODES, EVENTS } from \"./../constants\";\n\n/**\n * You can use dropdowns to display accessible contextual overlays for displaying lists of links and more.\n */\nclass Dropdown extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.handleListeners = this.handleListeners.bind(this);\n    this.addListeners = this.addListeners.bind(this);\n    this.removeListeners = this.removeListeners.bind(this);\n    this.handleDocumentClick = this.handleDocumentClick.bind(this);\n    this.getContainer = this.getContainer.bind(this);\n    this.toggle = this.toggle.bind(this);\n  }\n\n  componentDidMount() {\n    this.handleListeners();\n  }\n\n  componentWillUnmount() {\n    this.removeListeners();\n  }\n\n  componentDidUpdate(prevProps) {\n    if (this.props.open !== prevProps.open) {\n      this.handleListeners();\n    }\n  }\n\n  handleListeners() {\n    if (this.props.open) {\n      this.addListeners();\n      return;\n    }\n\n    this.removeListeners();\n  }\n\n  addListeners() {\n    EVENTS.CLICK.forEach(e =>\n      document.addEventListener(e, this.handleDocumentClick, true)\n    );\n  }\n\n  removeListeners() {\n    EVENTS.CLICK.forEach(e =>\n      document.removeEventListener(e, this.handleDocumentClick, true)\n    );\n  }\n\n  getContainer() {\n    return ReactDOM.findDOMNode(this); // eslint-disable-line react/no-find-dom-node\n  }\n\n  handleDocumentClick(e) {\n    if (\n      e &&\n      (e.which === 3 || (e.type === \"keyup\" && e.which !== KEYCODES.TAB))\n    )\n      return;\n    const container = this.getContainer();\n\n    if (\n      container.contains(e.target) &&\n      container !== e.target &&\n      (e.type !== \"keyup\" || e.which === KEYCODES.TAB)\n    ) {\n      return;\n    }\n\n    this.toggle(e);\n  }\n\n  toggle(e) {\n    if (this.props.disabled) {\n      return e && e.preventDefault();\n    }\n\n    return this.props.toggle(e);\n  }\n\n  render() {\n    const props = omit(this.props, [\n      \"toggle\",\n      \"disabled\",\n      \"inNavbar\",\n      \"direction\"\n    ]);\n\n    const {\n      className,\n      children,\n      dropup,\n      open,\n      group,\n      size,\n      nav,\n      setActiveFromChild,\n      active,\n      addonType,\n      ...attrs\n    } = props;\n\n    const direction =\n      this.props.direction === \"down\" && dropup ? \"up\" : this.props.direction;\n\n    attrs.tag = attrs.tag || (nav ? \"li\" : \"div\");\n\n    let subItemIsActive = false;\n    if (setActiveFromChild) {\n      React.Children.map(\n        this.props.children[1].props.children,\n        dropdownItem => {\n          if (dropdownItem && dropdownItem.props.active) subItemIsActive = true;\n        }\n      );\n    }\n\n    const classes = classNames(\n      className,\n      direction !== \"down\" && `drop${direction}`,\n      nav && active && \"active\",\n      setActiveFromChild && subItemIsActive && \"active\",\n      addonType && `input-group-${addonType}`,\n      group && \"btn-group\",\n      !!size && `btn-group-${size}`,\n      !group && !addonType && \"dropdown\",\n      open && \"show\",\n      nav && \"nav-item\"\n    );\n\n    const toggle = this.toggle;\n\n    return (\n      <DropdownContext.Provider value={{ toggle, open, direction, nav }}>\n        <Manager {...attrs}>\n          <DropdownContext.Consumer>\n            {() => <div className={classes}>{children}</div>}\n          </DropdownContext.Consumer>\n        </Manager>\n      </DropdownContext.Provider>\n    );\n  }\n}\n\nDropdown.propTypes = {\n  /**\n   * Whether it is open, or not.\n   */\n  open: PropTypes.bool,\n  /**\n   * Whether it is disabled, or not.\n   */\n  disabled: PropTypes.bool,\n  /**\n   * The toggle function.\n   */\n  toggle: PropTypes.func,\n  /**\n   * Whether it is located inside a navbar, or not.\n   */\n  inNavbar: PropTypes.bool,\n  /**\n   * Whether it is a drop-up, or not.\n   */\n  dropup: PropTypes.bool,\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.string,\n  /**\n   * Whether it is located inside a Nav, or not.\n   */\n  nav: PropTypes.bool,\n  /**\n   * The direction.\n   */\n  direction: PropTypes.oneOf([\"up\", \"down\", \"left\", \"right\"])\n};\n\nDropdown.defaultProps = {\n  open: false,\n  direction: \"down\",\n  nav: false\n};\n\nexport default Dropdown;\n",
    "examples": [{
        "name": "Dropdown",
        "filename": "01-basic-dropdown.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>You can create dropdowns using the <code>Dropdown</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem\n} from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * You can create dropdowns using the `Dropdown` component.\n */\nexport default class DropdownExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState(prevState => {\n      return { open: !prevState.open };\n    });\n  }\n\n  render() {\n    return (\n      <Dropdown open={this.state.open} toggle={this.toggle}>\n        <DropdownToggle>Dropdown</DropdownToggle>\n        <DropdownMenu>\n          <DropdownItem>Action</DropdownItem>\n          <DropdownItem>Another action</DropdownItem>\n          <DropdownItem>Something else here</DropdownItem>\n        </DropdownMenu>\n      </Dropdown>\n    );\n  }\n}\n"
    }, {
        "name": "Dropdown",
        "filename": "02-dropdown-themes.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Theme Color</h2>\n<p>Changing the theme color for the <code>DropdownToggle</code> component can be achieved via the <code>theme</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem\n} from \"shards-react\";\n\n/**\n * ## Theme Color\n *\n * Changing the theme color for the `DropdownToggle` component can be achieved via the `theme` prop.\n */\nexport default class DropdownThemeExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState(prevState => {\n      return { open: !prevState.open };\n    });\n  }\n\n  render() {\n    return (\n      <Dropdown open={this.state.open} toggle={this.toggle}>\n        <DropdownToggle theme=\"success\">Dropdown</DropdownToggle>\n        <DropdownMenu>\n          <DropdownItem>Action</DropdownItem>\n          <DropdownItem>Another action</DropdownItem>\n          <DropdownItem>Something else here</DropdownItem>\n        </DropdownMenu>\n      </Dropdown>\n    );\n  }\n}\n"
    }, {
        "name": "Dropdown",
        "filename": "03-dropdown-positioning.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Positioning</h2>\n<p>By default <code>DropdownMenu</code>s are left aligned. However, you can change this by applying a right alignment using the <code>right</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem\n} from \"shards-react\";\n\n/**\n * ## Positioning\n *\n * By default `DropdownMenu`s are left aligned. However, you can change this by applying a right alignment using the `right` prop.\n */\nexport default class DropdownPositionExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState(prevState => {\n      return { open: !prevState.open };\n    });\n  }\n\n  render() {\n    return (\n      <Dropdown open={this.state.open} toggle={this.toggle} className=\"d-table\">\n        <DropdownToggle>Right Align</DropdownToggle>\n        <DropdownMenu right>\n          <DropdownItem>Action</DropdownItem>\n          <DropdownItem>Another action</DropdownItem>\n          <DropdownItem>Something else here</DropdownItem>\n        </DropdownMenu>\n      </Dropdown>\n    );\n  }\n}\n"
    }, {
        "name": "Dropdown",
        "filename": "04-dropup.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Drop-up</h2>\n<p>Turning dropdown menus into drop-up menus can be easily achieved using the <code>dropup</code> prop applied on the <code>Dropdown</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem\n} from \"shards-react\";\n\n/**\n * ## Drop-up\n *\n * Turning dropdown menus into drop-up menus can be easily achieved using the `dropup` prop applied on the `Dropdown` component.\n */\nexport default class DropupExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState(prevState => {\n      return { open: !prevState.open };\n    });\n  }\n\n  render() {\n    return (\n      <Dropdown open={this.state.open} toggle={this.toggle} dropup>\n        <DropdownToggle>Dropup</DropdownToggle>\n        <DropdownMenu>\n          <DropdownItem>Action</DropdownItem>\n          <DropdownItem>Another action</DropdownItem>\n          <DropdownItem>Something else here</DropdownItem>\n        </DropdownMenu>\n      </Dropdown>\n    );\n  }\n}\n"
    }, {
        "name": "Dropdown",
        "filename": "05-split-buttons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Split Buttons</h2>\n<p>Using the <code>group</code> prop applied on the <code>Dropdown</code> component and the <code>split</code> prop applied on the <code>DropdownToggle</code> component you can create split-type dropdowns.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem,\n  Button\n} from \"shards-react\";\n\n/**\n * ## Split Buttons\n *\n * Using the `group` prop applied on the `Dropdown` component and the `split` prop applied on the `DropdownToggle` component you can create split-type dropdowns.\n */\nexport default class DropdownSplitExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState(prevState => {\n      return { open: !prevState.open };\n    });\n  }\n\n  render() {\n    return (\n      <Dropdown open={this.state.open} toggle={this.toggle} group>\n        <Button>Dropdown</Button>\n        <DropdownToggle split />\n        <DropdownMenu>\n          <DropdownItem>Action</DropdownItem>\n          <DropdownItem>Another action</DropdownItem>\n          <DropdownItem>Something else here</DropdownItem>\n        </DropdownMenu>\n      </Dropdown>\n    );\n  }\n}\n"
    }, {
        "name": "Dropdown",
        "filename": "06-dropdown-sizing.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Sizing</h2>\n<p>Using the <code>size</code> prop on the <code>Dropdown</code> component you can control the dropdown button's size.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem\n} from \"shards-react\";\n\n/**\n * ## Sizing\n *\n * Using the `size` prop on the `Dropdown` component you can control the dropdown button's size.\n */\nexport default class DropdownSizeExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = {\n      dropdown1: false,\n      dropdown2: false,\n      dropdown3: false\n    };\n  }\n\n  toggle(nr) {\n    this.setState(prevState => {\n      const newState = {};\n      newState[`dropdown${nr}`] = !prevState[`dropdown${nr}`];\n      return { ...prevState, ...newState };\n    });\n  }\n\n  render() {\n    return (\n      <div style={{ display: \"flex\" }}>\n        <Dropdown\n          open={this.state.dropdown1}\n          toggle={() => this.toggle(1)}\n          size=\"lg\"\n          className=\"mr-2\"\n        >\n          <DropdownToggle caret>Large</DropdownToggle>\n          <DropdownMenu>\n            <DropdownItem>Action</DropdownItem>\n            <DropdownItem>Another action</DropdownItem>\n            <DropdownItem>Something else here</DropdownItem>\n          </DropdownMenu>\n        </Dropdown>\n\n        <Dropdown\n          open={this.state.dropdown2}\n          toggle={() => this.toggle(2)}\n          className=\"mr-2\"\n        >\n          <DropdownToggle caret>Normal</DropdownToggle>\n          <DropdownMenu>\n            <DropdownItem>Action</DropdownItem>\n            <DropdownItem>Another action</DropdownItem>\n            <DropdownItem>Something else here</DropdownItem>\n          </DropdownMenu>\n        </Dropdown>\n\n        <Dropdown\n          open={this.state.dropdown3}\n          toggle={() => this.toggle(3)}\n          size=\"sm\"\n          className=\"mr-2\"\n        >\n          <DropdownToggle caret>Small</DropdownToggle>\n          <DropdownMenu>\n            <DropdownItem>Action</DropdownItem>\n            <DropdownItem>Another action</DropdownItem>\n            <DropdownItem>Something else here</DropdownItem>\n          </DropdownMenu>\n        </Dropdown>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "DropdownItem",
        "methods": [{
            "name": "onClick",
            "docblock": null,
            "modifiers": [],
            "params": [{
                "name": "e",
                "type": null
            }],
            "returns": null
        }, {
            "name": "getTabIndex",
            "docblock": null,
            "modifiers": [],
            "params": [],
            "returns": null
        }],
        "props": {
            "children": {
                "type": {
                    "name": "node"
                },
                "required": false,
                "description": "The children nodes."
            },
            "active": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is active, or not."
            },
            "disabled": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is disabled, or not."
            },
            "divider": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is a divider, or not."
            },
            "header": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is a dropdown header item, or not."
            },
            "onClick": {
                "type": {
                    "name": "func"
                },
                "required": false,
                "description": "The function that should be triggered on click."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "toggle": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it should toggle the dropdown, or not.",
                "defaultValue": {
                    "value": "true",
                    "computed": false
                }
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"button\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "DropdownMenu",
        "methods": [],
        "props": {
            "tag": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The component tag.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            },
            "children": {
                "type": {
                    "name": "node"
                },
                "required": true,
                "description": "The children nodes."
            },
            "right": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is positioned on the right side, or not."
            },
            "flip": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it should flip, or not.",
                "defaultValue": {
                    "value": "true",
                    "computed": false
                }
            },
            "small": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether the dropdown is small, or not."
            },
            "modifiers": {
                "type": {
                    "name": "object"
                },
                "required": false,
                "description": "The modifiers config object."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "persist": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it should persist, or not."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "DropdownToggle",
        "methods": [{
            "name": "onClick",
            "docblock": null,
            "modifiers": [],
            "params": [{
                "name": "e",
                "type": null
            }],
            "returns": null
        }],
        "props": {
            "caret": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it should display a caret, or not."
            },
            "theme": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The theme color.",
                "defaultValue": {
                    "value": "\"primary\"",
                    "computed": false
                }
            },
            "children": {
                "type": {
                    "name": "node"
                },
                "required": false,
                "description": "The children nodes."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "disabled": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is disabled, or not."
            },
            "onClick": {
                "type": {
                    "name": "func"
                },
                "required": false,
                "description": "The function that should be triggered on click."
            },
            "aria-haspopup": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "The aria-haspopup attribute.",
                "defaultValue": {
                    "value": "true",
                    "computed": false
                }
            },
            "split": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is split, or not."
            },
            "nav": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is located inside a nav, or not."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type."
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>You can use dropdowns to display accessible contextual overlays for displaying lists of links and more.</p>\n"
    },
    "props": {
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is open, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is disabled, or not."
        },
        "toggle": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The toggle function."
        },
        "inNavbar": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is located inside a navbar, or not."
        },
        "dropup": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is a drop-up, or not."
        },
        "tag": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The component's tag type."
        },
        "nav": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is located inside a Nav, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "direction": {
            "type": {
                "name": "enum",
                "value": [{
                    "value": "\"up\"",
                    "computed": false
                }, {
                    "value": "\"down\"",
                    "computed": false
                }, {
                    "value": "\"left\"",
                    "computed": false
                }, {
                    "value": "\"right\"",
                    "computed": false
                }]
            },
            "required": false,
            "description": "The direction.",
            "defaultValue": {
                "value": "\"down\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/fade",
    "name": "Fade",
    "filename": "Fade.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport Transition from \"react-transition-group/Transition\";\nimport omit from \"lodash.omit\";\nimport pick from \"lodash.pick\";\n\nimport { TIMEOUT, TRANSITION_KEYS } from \"./../constants\";\n\n/**\n * The `Fade` component allows you to easily fade in and out content and is powered by [react-transition-group](https://github.com/reactjs/react-transition-group).\n */\nconst Fade = props => {\n  const {\n    tag: Tag,\n    baseClass,\n    baseClassActive,\n    className,\n    children,\n    innerRef,\n    ...attrs\n  } = props;\n\n  const transitionProps = pick(attrs, TRANSITION_KEYS);\n  const childProps = omit(attrs, TRANSITION_KEYS);\n\n  return (\n    <Transition {...transitionProps}>\n      {status => {\n        const isActive = status === \"entered\";\n        const classes = classNames(\n          className,\n          baseClass,\n          isActive && baseClassActive\n        );\n        return (\n          <Tag className={classes} {...childProps} ref={innerRef}>\n            {children}\n          </Tag>\n        );\n      }}\n    </Transition>\n  );\n};\n\nFade.propTypes = {\n  ...Transition.propTypes,\n  tag: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),\n  baseClass: PropTypes.string,\n  baseClassActive: PropTypes.string,\n  className: PropTypes.string,\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.string,\n    PropTypes.func\n  ]),\n  children: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.node),\n    PropTypes.node\n  ])\n};\n\nFade.defaultProps = {\n  ...Transition.defaultProps,\n  tag: \"div\",\n  baseClass: \"fade\",\n  baseClassActive: \"show\",\n  timeout: TIMEOUT.FADE,\n  appear: true,\n  enter: true,\n  exit: true,\n  in: true\n};\n\nexport default Fade;\n",
    "examples": [{
        "name": "Fade",
        "filename": "01-basic-fade.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n"
        },
        "code": "import React from \"react\";\nimport { Fade, Button } from \"shards-react\";\n\n/**\n * ## Basic Example\n */\nexport default class BasicFadeExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = {\n      visible: true\n    };\n  }\n\n  toggle() {\n    this.setState({\n      visible: !this.state.visible\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button onClick={this.toggle} className=\"mb-2\">\n          Toggle Fade\n        </Button>\n        <Fade in={this.state.visible}>\n          Etiam semper convallis tortor, in euismod orci vehicula sit amet.\n        </Fade>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>Fade</code> component allows you to easily fade in and out content and is powered by <a href=\"https://github.com/reactjs/react-transition-group\">react-transition-group</a>.</p>\n"
    },
    "props": {
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "func"
                }]
            },
            "required": false,
            "description": "",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        },
        "baseClass": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "",
            "defaultValue": {
                "value": "\"fade\"",
                "computed": false
            }
        },
        "baseClassActive": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "",
            "defaultValue": {
                "value": "\"show\"",
                "computed": false
            }
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": ""
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "string"
                }, {
                    "name": "func"
                }]
            },
            "required": false,
            "description": ""
        },
        "children": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "node"
                    }
                }, {
                    "name": "node"
                }]
            },
            "required": false,
            "description": ""
        },
        "timeout": {
            "defaultValue": {
                "value": "TIMEOUT.FADE",
                "computed": true
            },
            "required": false
        },
        "appear": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        },
        "enter": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        },
        "exit": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        },
        "in": {
            "defaultValue": {
                "value": "true",
                "computed": false
            },
            "required": false
        }
    }
}, {
    "slug": "component/form",
    "name": "Form",
    "filename": "Form.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Examples and usage guidelines for form controls.\n */\nclass Form extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.getRef = this.getRef.bind(this);\n    this.submit = this.submit.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  submit() {\n    if (this.ref) {\n      this.ref.submit();\n    }\n  }\n\n  render() {\n    const { className, tag: Tag, inline, innerRef, ...attrs } = this.props;\n    const classes = classNames(className, inline && \"form-inline\");\n\n    return <Tag {...attrs} ref={innerRef} className={classes} />;\n  }\n}\n\nForm.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * Whether it is inline, or not.\n   */\n  inline: PropTypes.bool,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.node),\n    PropTypes.node\n  ]),\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ]),\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nForm.defaultProps = {\n  tag: \"form\"\n};\n\nexport default Form;\n",
    "examples": [{
        "name": "Form",
        "filename": "01-basic-form.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>Forms can be created using the <code>Form</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Form, FormInput, FormGroup } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * Forms can be created using the `Form` component.\n */\nexport default function FormExample() {\n  return (\n    <Form>\n      <FormGroup>\n        <label htmlFor=\"#username\">Username</label>\n        <FormInput id=\"#username\" placeholder=\"Username\" />\n      </FormGroup>\n      <FormGroup>\n        <label htmlFor=\"#password\">Password</label>\n        <FormInput type=\"password\" id=\"#password\" placeholder=\"Password\" />\n      </FormGroup>\n    </Form>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "FormFeedback",
        "methods": [],
        "props": {
            "children": {
                "type": {
                    "name": "node"
                },
                "required": false,
                "description": "The children."
            },
            "tag": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "valid": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether the feedback is valid, or not.",
                "defaultValue": {
                    "value": "undefined",
                    "computed": true
                }
            },
            "tooltip": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether the feedback should be displayed as tooltip."
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Examples and usage guidelines for form controls.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "inline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is inline, or not."
        },
        "children": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "node"
                    }
                }, {
                    "name": "node"
                }]
            },
            "required": false,
            "description": "The children nodes."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"form\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/form-checkbox",
    "name": "FormCheckbox",
    "filename": "FormCheckbox.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport shortid from \"shortid\";\n\n/**\n * The `FormCheckbox` component is a wrapper over Bootstrap's [custom checkbox component](https://getbootstrap.com/docs/4.1/components/forms/#checkboxes-and-radios-1).\n */\nclass FormCheckbox extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.getRef = this.getRef.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  render() {\n    const {\n      className,\n      children,\n      inline,\n      valid,\n      invalid,\n      innerRef,\n      toggle,\n      small,\n      id: _id,\n      ...attrs\n    } = this.props;\n\n    const labelClasses = classNames(\n      className,\n      \"custom-control\",\n      !toggle ? \"custom-checkbox\" : \"custom-toggle\",\n      toggle && small && \"custom-toggle-sm\",\n      inline && \"custom-control-inline\",\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    const inputClasses = classNames(\n      \"custom-control-input\",\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    const id = _id || `dr-checkbox-${shortid.generate()}`;\n\n    return (\n      <label className={labelClasses}>\n        <input\n          {...attrs}\n          ref={innerRef}\n          id={id}\n          type=\"checkbox\"\n          className={inputClasses}\n        />\n        <label id={id} className=\"custom-control-label\" aria-hidden=\"true\" onClick={this.props.onChange} />\n        <span className=\"custom-control-description\">{children}</span>\n      </label>\n    );\n  }\n}\n\nFormCheckbox.defaultProps = {\n  onChange: () => {}\n}\n\nFormCheckbox.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * Whether it is inline, or not.\n   */\n  inline: PropTypes.bool,\n  /**\n   * Whether it is valid, or not.\n   */\n  valid: PropTypes.bool,\n  /**\n   * Whether it is invalid, or not.\n   */\n  invalid: PropTypes.bool,\n  /**\n   * Whether it is a toggle button, or not.\n   */\n  toggle: PropTypes.bool,\n  /**\n   * Whether it is small (toggle), or not.\n   */\n  small: PropTypes.bool,\n  /**\n   * The onChange handler.\n   */\n  onChange: PropTypes.func,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nexport default FormCheckbox;\n",
    "examples": [{
        "name": "FormCheckbox",
        "filename": "01-basic-form-checkbox.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Checkbox</h2>\n"
        },
        "code": "import React from \"react\";\nimport { FormCheckbox } from \"shards-react\";\n\n/**\n * ## Basic Checkbox\n */\nexport default class FormCheckboxExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.handleChange = this.handleChange.bind(this);\n    this.state = {\n      orange: false,\n      lemon: false,\n      kiwi: false\n    };\n  }\n\n  handleChange(e, fruit) {\n    const newState = {};\n    newState[fruit] = !this.state[fruit];\n    this.setState({ ...this.state, ...newState });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Select your favorite fruits:</p>\n        <FormCheckbox\n          checked={this.state.orange}\n          onChange={e => this.handleChange(e, \"orange\")}\n        >\n          Orange\n        </FormCheckbox>\n        <FormCheckbox\n          checked={this.state.lemon}\n          onChange={e => this.handleChange(e, \"lemon\")}\n        >\n          Lemon\n        </FormCheckbox>\n        <FormCheckbox\n          checked={this.state.kiwi}\n          onChange={e => this.handleChange(e, \"kiwi\")}\n        >\n          Kiwi\n        </FormCheckbox>\n        <span className=\"d-block mt-3\">\n          <strong>Selected fruits:</strong>{\" \"}\n          <pre>{JSON.stringify(this.state)}</pre>\n        </span>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "FormCheckbox",
        "filename": "02-basic-toggles.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Toggles</h2>\n<p>Checkboxes can be turned into toggles by using the <code>toggle</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormCheckbox } from \"shards-react\";\n\n/**\n * ## Basic Toggles\n *\n * Checkboxes can be turned into toggles by using the `toggle` prop.\n */\nclass BasicToggleExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      checked: false\n    };\n\n    this.handleChange = this.handleChange.bind(this);\n  }\n\n  handleChange() {\n    this.setState({\n      checked: !this.state.checked\n    });\n  }\n\n  render() {\n    return (\n      <FormCheckbox\n        toggle\n        checked={this.state.checked}\n        onChange={this.handleChange}>\n        🚀 Enable Rockets\n      </FormCheckbox>\n    );\n  }\n}\n\nexport default BasicToggleExample;\n"
    }, {
        "name": "FormCheckbox",
        "filename": "03-small-toggles.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Small Toggles</h2>\n<p>A toggle's size can be adjusted using the <code>small</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormCheckbox } from \"shards-react\";\n\n/**\n * ## Small Toggles\n *\n * A toggle's size can be adjusted using the `small` prop.\n */\nclass SmallToggleExamples extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      checked: false\n    };\n\n    this.handleChange = this.handleChange.bind(this);\n  }\n\n  handleChange() {\n    this.setState({\n      checked: !this.state.checked\n    });\n  }\n\n  render() {\n    return (\n      <FormCheckbox\n        toggle\n        small\n        checked={this.state.checked}\n        onChange={this.handleChange}>\n        🚀 Enable (small) Rockets\n      </FormCheckbox>\n    );\n  }\n}\n\nexport default SmallToggleExamples;\n\n"
    }, {
        "name": "FormCheckbox",
        "filename": "04-inline-checkboxes.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Inline Display</h2>\n<p>Checkboxes can also be displayed inline using the <code>inline</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormCheckbox } from \"shards-react\";\n\n/**\n * ## Inline Display\n *\n * Checkboxes can also be displayed inline using the `inline` prop.\n */\nexport default class InlineCheckboxes extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.handleChange = this.handleChange.bind(this);\n\n    this.state = {\n      basketball: false,\n      football: false,\n      tennis: false\n    };\n  }\n\n  handleChange(e, fruit) {\n    const newState = {};\n    newState[fruit] = !this.state[fruit];\n    this.setState({ ...this.state, ...newState });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Select your favorite fruits:</p>\n        <FormCheckbox\n          inline\n          checked={this.state.basketball}\n          onChange={e => this.handleChange(e, \"basketball\")}\n        >\n          Basketball\n        </FormCheckbox>\n        <FormCheckbox\n          inline\n          checked={this.state.football}\n          onChange={e => this.handleChange(e, \"football\")}\n        >\n          Football\n        </FormCheckbox>\n        <FormCheckbox\n          inline\n          checked={this.state.tennis}\n          onChange={e => this.handleChange(e, \"tennis\")}\n        >\n          Tennis\n        </FormCheckbox>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>FormCheckbox</code> component is a wrapper over Bootstrap's <a href=\"https://getbootstrap.com/docs/4.1/components/forms/#checkboxes-and-radios-1\">custom checkbox component</a>.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "inline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is inline, or not."
        },
        "valid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is valid, or not."
        },
        "invalid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is invalid, or not."
        },
        "toggle": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is a toggle button, or not."
        },
        "small": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is small (toggle), or not."
        },
        "onChange": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The onChange handler.",
            "defaultValue": {
                "value": "() => {}",
                "computed": false
            }
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/form-group",
    "name": "FormGroup",
    "filename": "FormGroup.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Form groups allow you to easily add structure to your forms.\n */\nconst FormGroup = props => {\n  const { className, row, disabled, check, inline, tag: Tag, ...attrs } = props;\n  const classes = classNames(\n    className,\n    row && \"row\",\n    check ? \"form-check\" : \"form-group\",\n    check && inline && \"form-check-inline\",\n    check && disabled && \"disabled\"\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nFormGroup.propTypes = {\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * Whether it is a row, or not.\n   */\n  row: PropTypes.bool,\n  /**\n   * Whether it is a form check, or not.\n   */\n  check: PropTypes.bool,\n  /**\n   * Whether it is displayed inline (form check) or not.\n   */\n  inline: PropTypes.bool,\n  /**\n   * Whether it is disabled, or not.\n   */\n  disabled: PropTypes.bool,\n  /**\n   * The components' tag type.\n   */\n  tag: PropTypes.string,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string\n};\n\nFormGroup.defaultProps = {\n  tag: \"div\"\n};\n\nexport default FormGroup;\n",
    "examples": [{
        "name": "FormGroup",
        "filename": "01-basic-form-group.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Form Group</h2>\n"
        },
        "code": "import React from \"react\";\nimport { Form, FormGroup, FormInput } from \"shards-react\";\n\n/**\n * ## Basic Form Group\n */\nexport default function FormGroupExample() {\n  return (\n    <Form>\n      <FormGroup>\n        <label htmlFor=\"username\">Username</label>\n        <FormInput id=\"username\" />\n      </FormGroup>\n    </Form>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Form groups allow you to easily add structure to your forms.</p>\n"
    },
    "props": {
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "row": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is a row, or not."
        },
        "check": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is a form check, or not."
        },
        "inline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is displayed inline (form check) or not."
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is disabled, or not."
        },
        "tag": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The components' tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        }
    }
}, {
    "slug": "component/form-input",
    "name": "FormInput",
    "filename": "FormInput.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\nimport { INPUT_TYPES } from \"../constants\";\n\n/**\n * The form input allows you to create various text style inputs such as `text`, `password`, `email`, `number`, `url`, `search` and more.\n */\nclass FormInput extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.ref = null;\n\n    this.getRef = this.getRef.bind(this);\n    this.focus = this.focus.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  focus() {\n    if (this.ref) {\n      this.ref.focus();\n    }\n  }\n\n  render() {\n    const {\n      className,\n      plaintext,\n      size,\n      invalid,\n      valid,\n      innerRef,\n      ...attrs\n    } = this.props;\n\n    const classes = classNames(\n      className,\n      plaintext ? \"form-control-plaintext\" : \"form-control\",\n      plaintext && \"w-100\",\n      size && `form-control-${size}`,\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    return <input {...attrs} ref={innerRef} className={classes} />;\n  }\n}\n\nFormInput.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * Whether it is inline, or not.\n   */\n  inline: PropTypes.bool,\n  /**\n   * The input type.\n   */\n  type: PropTypes.oneOf(INPUT_TYPES),\n  /**\n   * Whether it is plaintext, or not.\n   */\n  plaintext: PropTypes.bool,\n  /**\n   * The input's size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it is valid, or not.\n   */\n  valid: PropTypes.bool,\n  /**\n   * Whether it is invalid, or not.\n   */\n  invalid: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nexport default FormInput;\n",
    "examples": [{
        "name": "FormInput",
        "filename": "01-basic-form-input.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Form Input</h2>\n<p>The <code>FormInput</code> component is a <code>text</code> input by default. However, you can set its type prop to one of the supported types as well: <code>password</code>, <code>email</code>, <code>number</code>, <code>url</code>, <code>tel</code>, <code>search</code>, <code>date</code>, <code>datetime</code>, <code>datetime-local</code>, <code>month</code>, <code>week</code>, <code>time</code>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormInput } from \"shards-react\";\n\n/**\n * ## Form Input\n *\n * The `FormInput` component is a `text` input by default. However, you can set its type prop to one of the supported types as well: `password`, `email`, `number`, `url`, `tel`, `search`, `date`, `datetime`, `datetime-local`, `month`, `week`, `time`.\n */\nexport default function FormInputExample() {\n  return <FormInput placeholder=\"My form input\" />;\n}\n"
    }, {
        "name": "FormInput",
        "filename": "02-form-input-sizing.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Sizing</h2>\n<p>Using the <code>size</code> prop, you can change the input size as small (<code>sm</code>) or large (<code>lg</code>).</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormInput } from \"shards-react\";\n\n/**\n * ## Sizing\n *\n * Using the `size` prop, you can change the input size as small (`sm`) or large (`lg`).\n */\nexport default function FormInputSizeExample() {\n  return (\n    <div>\n      <FormInput size=\"sm\" placeholder=\"Small input\" className=\"mb-2\" />\n      <FormInput placeholder=\"Normal input\" className=\"mb-2\" />\n      <FormInput size=\"lg\" placeholder=\"Large input\" />\n    </div>\n  );\n}\n"
    }, {
        "name": "FormInput",
        "filename": "03-form-input-validation.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Validation States</h2>\n<p>Using the <code>valid</code> or <code>invalid</code> props you can control the input's validation state.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormInput } from \"shards-react\";\n\n/**\n * ## Validation States\n *\n * Using the `valid` or `invalid` props you can control the input's validation state.\n */\nexport default function FormValidationStateExample() {\n  return (\n    <div>\n      <FormInput placeholder=\"I'm neutral\" className=\"mb-2\" />\n      <FormInput valid placeholder=\"I'm valid\" className=\"mb-2\" />\n      <FormInput invalid placeholder=\"I'm invalid\" />\n    </div>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The form input allows you to create various text style inputs such as <code>text</code>, <code>password</code>, <code>email</code>, <code>number</code>, <code>url</code>, <code>search</code> and more.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "inline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is inline, or not."
        },
        "type": {
            "type": {
                "name": "enum",
                "computed": true,
                "value": "INPUT_TYPES"
            },
            "required": false,
            "description": "The input type."
        },
        "plaintext": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is plaintext, or not."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The input's size."
        },
        "valid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is valid, or not."
        },
        "invalid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is invalid, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/form-radio",
    "name": "FormRadio",
    "filename": "FormRadio.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport shortid from \"shortid\";\n\nclass FormRadio extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.getRef = this.getRef.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  render() {\n    const {\n      className,\n      children,\n      inline,\n      valid,\n      invalid,\n      innerRef,\n      onChange,\n      id: _id,\n      ...attrs\n    } = this.props;\n\n    const labelClasses = classNames(\n      \"custom-control\",\n      \"custom-radio\",\n      inline && \"custom-control-inline\",\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    const inputClasses = classNames(\n      className,\n      \"custom-control-input\",\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    const id = _id || `dr-radio-${shortid.generate()}`;\n\n    return (\n      <label className={labelClasses}>\n        <input\n          {...attrs}\n          ref={innerRef}\n          id={id}\n          type=\"radio\"\n          className={inputClasses}\n          onChange={onChange}\n        />\n        <label id={id} className=\"custom-control-label\" aria-hidden=\"true\" onClick={onChange} />\n        <span className=\"custom-control-description\">{children}</span>\n      </label>\n    );\n  }\n}\n\nFormRadio.defaultProps = {\n  onChange: () => {}\n};\n\nFormRadio.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children.\n   */\n  children: PropTypes.node,\n  /**\n   * Whether it is inline, or not.\n   */\n  inline: PropTypes.bool,\n  /**\n   * Whether it is valid, or not.\n   */\n  valid: PropTypes.bool,\n  /**\n   * The function that should run on change.\n   */\n  onChange: PropTypes.func,\n  /**\n   * Whether it is invalid, or not.\n   */\n  invalid: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nexport default FormRadio;\n",
    "examples": [{
        "name": "FormRadio",
        "filename": "01-basic-form-radio.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Demo</h2>\n<p>The <code>FormRadio</code> component is a wrapper over Bootstrap's <a href=\"https://getbootstrap.com/docs/4.1/components/forms/#checkboxes-and-radios-1\">custom radio component</a>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormRadio } from \"shards-react\";\n\n/**\n * ## Basic Demo\n *\n * The `FormRadio` component is a wrapper over Bootstrap's [custom radio component](https://getbootstrap.com/docs/4.1/components/forms/#checkboxes-and-radios-1).\n */\nexport default class FormRadioExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      selectedFruit: null\n    };\n\n    this.changeFruit = this.changeFruit.bind(this);\n  }\n\n  changeFruit(fruit) {\n    this.setState({\n      selectedFruit: fruit\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p className=\"mb-2\">Select your favorite fruit:</p>\n        <FormRadio\n          name=\"fruit\"\n          checked={this.state.selectedFruit === \"orange\"}\n          onChange={() => {\n            this.changeFruit(\"orange\");\n          }}\n        >\n          Orange\n        </FormRadio>\n        <FormRadio\n          name=\"fruit\"\n          checked={this.state.selectedFruit === \"lemon\"}\n          onChange={() => {\n            this.changeFruit(\"lemon\");\n          }}\n        >\n          Lemon\n        </FormRadio>\n        <FormRadio\n          name=\"fruit\"\n          checked={this.state.selectedFruit === \"kiwi\"}\n          onChange={() => {\n            this.changeFruit(\"kiwi\");\n          }}\n        >\n          Kiwi\n        </FormRadio>\n        <span>\n          <strong>Selected fruit:</strong>{\" \"}\n          <span>{this.state.selectedFruit || \"none\"}</span>\n        </span>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "FormRadio",
        "filename": "02-inline-form-radio.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Inline Display</h2>\n<p>Radios can also be displayed inline using the <code>inline</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormRadio } from \"shards-react\";\n\n/**\n * ## Inline Display\n *\n * Radios can also be displayed inline using the `inline` prop.\n */\nexport default class FormRadioExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      selectedSport: null\n    };\n\n    this.changeSport = this.changeSport.bind(this);\n  }\n\n  changeSport(sport) {\n    this.setState({\n      selectedSport: sport\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <div>\n          <p className=\"mb-2\">Select your favorite sport:</p>\n          <FormRadio\n            inline\n            name=\"sport\"\n            checked={this.state.selectedSport === \"basketball\"}\n            onChange={() => {\n              this.changeSport(\"basketball\");\n            }}\n          >\n            Basketball\n          </FormRadio>\n          <FormRadio\n            inline\n            name=\"sport\"\n            checked={this.state.selectedSport === \"football\"}\n            onChange={() => {\n              this.changeSport(\"football\");\n            }}\n          >\n            Football\n          </FormRadio>\n          <FormRadio\n            inline\n            name=\"sport\"\n            checked={this.state.selectedSport === \"tennis\"}\n            onChange={() => {\n              this.changeSport(\"tennis\");\n            }}\n          >\n            Tennis\n          </FormRadio>\n        </div>\n        <span>\n          <strong>Selected sport:</strong>{\" \"}\n          <span>{this.state.selectedSport || \"none\"}</span>\n        </span>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children."
        },
        "inline": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is inline, or not."
        },
        "valid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is valid, or not."
        },
        "onChange": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The function that should run on change.",
            "defaultValue": {
                "value": "() => {}",
                "computed": false
            }
        },
        "invalid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is invalid, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/form-select",
    "name": "FormSelect",
    "filename": "FormSelect.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * The `FormSelect` component is a wrapper over Bootstrap's [custom select component](https://getbootstrap.com/docs/4.1/components/forms/#select-menu).\n */\nclass FormSelect extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.getRef = this.getRef.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  render() {\n    const {\n      className,\n      children,\n      size,\n      valid,\n      invalid,\n      innerRef,\n      ...attrs\n    } = this.props;\n\n    const classes = classNames(\n      className,\n      \"form-control\",\n      \"custom-select\",\n      valid && \"is-valid\",\n      invalid && \"is-invalid\",\n      size && `form-control-${size}`,\n      size && `custom-select-${size}`\n    );\n\n    return (\n      <select {...attrs} className={classes} ref={innerRef}>\n        {children}\n      </select>\n    );\n  }\n}\n\nFormSelect.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it is valid, or not.\n   */\n  valid: PropTypes.bool,\n  /**\n   * Whether it is invalid, or not.\n   */\n  invalid: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nexport default FormSelect;\n",
    "examples": [{
        "name": "FormSelect",
        "filename": "01-basic-form-select.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Form Select</h2>\n<p>Select components can be created using the <code>FormSelect</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormSelect } from \"shards-react\";\n\n/**\n * ## Form Select\n *\n * Select components can be created using the `FormSelect` component.\n */\nexport default function FormSelectDemo() {\n  return (\n    <FormSelect>\n      <option value=\"first\">This is the first option</option>\n      <option value=\"second\">This is the second option.</option>\n      <option value=\"third\" disabled>\n        This option is disabled.\n      </option>\n    </FormSelect>\n  );\n}\n"
    }, {
        "name": "FormSelect",
        "filename": "02-form-select-sizing.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Sizing</h2>\n<p>You can control the form-control's size using the <code>size</code> prop which accepts <code>sm</code> for small or <code>lg</code> for large.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormSelect } from \"shards-react\";\n\n/**\n * ## Sizing\n *\n * You can control the form-control's size using the `size` prop which accepts `sm` for small or `lg` for large.\n */\nexport default function FormSelectSizingDemo() {\n  return (\n    <div>\n      <FormSelect size=\"sm\" className=\"mb-2\">\n        <option value=\"first\">This is the first option</option>\n        <option value=\"second\">This is the second option.</option>\n        <option value=\"third\" disabled>\n          This option is disabled.\n        </option>\n      </FormSelect>\n      <FormSelect size=\"lg\">\n        <option value=\"first\">This is the first option</option>\n        <option value=\"second\">This is the second option.</option>\n        <option value=\"third\" disabled>\n          This option is disabled.\n        </option>\n      </FormSelect>\n    </div>\n  );\n}\n"
    }, {
        "name": "FormSelect",
        "filename": "03-form-select-validation.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Validation</h2>\n<p>Using the <code>valid</code> and <code>invalid</code> props you can control the current active validation state.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FormSelect } from \"shards-react\";\n\n/**\n * ## Validation\n *\n * Using the `valid` and `invalid` props you can control the current active validation state.\n */\nexport default function FormSelectValidationDemo() {\n  return (\n    <div>\n      <FormSelect className=\"mb-2\">\n        <option value=\"first\">I am neutral</option>\n      </FormSelect>\n      <FormSelect className=\"mb-2\" invalid>\n        <option value=\"first\">I am invalid</option>\n      </FormSelect>\n      <FormSelect valid>\n        <option value=\"first\">I am valid</option>\n      </FormSelect>\n    </div>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>FormSelect</code> component is a wrapper over Bootstrap's <a href=\"https://getbootstrap.com/docs/4.1/components/forms/#select-menu\">custom select component</a>.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "valid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is valid, or not."
        },
        "invalid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is invalid, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/form-textarea",
    "name": "FormTextarea",
    "filename": "FormTextarea.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * The `FormTextarea` component allows you to easily create multi-line text inputs.\n */\nclass FormTextarea extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.getRef = this.getRef.bind(this);\n  }\n\n  getRef(ref) {\n    if (this.props.innerRef) {\n      this.props.innerRef(ref);\n    }\n\n    this.ref = ref;\n  }\n\n  render() {\n    const {\n      className,\n      children,\n      innerRef,\n      plaintext,\n      size,\n      valid,\n      invalid,\n      ...attrs\n    } = this.props;\n\n    const classes = classNames(\n      className,\n      children,\n      plaintext ? \"form-control-plaintext\" : \"form-control\",\n      plaintext && \"w-100\",\n      size && `form-control-${size}`,\n      valid && \"is-valid\",\n      invalid && \"is-invalid\"\n    );\n\n    return <textarea {...attrs} className={classes} ref={innerRef} />;\n  }\n}\n\nFormTextarea.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it should be displayed as plain text, or not.\n   */\n  plaintext: PropTypes.bool,\n  /**\n   * Whether it is valid, or not.\n   */\n  valid: PropTypes.bool,\n  /**\n   * Whether it is invalid, or not.\n   */\n  invalid: PropTypes.bool,\n  /**\n   * The inner ref.\n   */\n  innerRef: PropTypes.oneOfType([\n    PropTypes.object,\n    PropTypes.func,\n    PropTypes.string\n  ])\n};\n\nexport default FormTextarea;\n",
    "examples": [{
        "name": "FormTextarea",
        "filename": "01-basic-form-textarea.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n"
        },
        "code": "import React from \"react\";\nimport { FormTextarea } from \"shards-react\";\n\n/**\n * ## Basic Example\n */\nexport default class BasicFormTextarea extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleChange = this.handleChange.bind(this);\n    this.state = { value: null };\n  }\n\n  handleChange(e) {\n    this.setState({ value: e.target.value });\n  }\n\n  render() {\n    const { value } = this.state;\n    return (\n      <div>\n        <p className=\"mb-2\">\n          {(value && `🗣 ${value}`) || \"🤔 Waiting for you to say something...\"}\n        </p>\n        <FormTextarea onChange={this.handleChange} />\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>FormTextarea</code> component allows you to easily create multi-line text inputs.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "plaintext": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be displayed as plain text, or not."
        },
        "valid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is valid, or not."
        },
        "invalid": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is invalid, or not."
        },
        "innerRef": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "object"
                }, {
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The inner ref."
        }
    }
}, {
    "slug": "component/input-group",
    "name": "InputGroup",
    "filename": "InputGroup.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Using the `InputGroup` component you can easily extend form controls by adding various elements such as text, buttons and button groups.\n */\nconst InputGroup = props => {\n  const { className, tag: Tag, size, seamless, ...attrs } = props;\n  const classes = classNames(\n    className,\n    \"input-group\",\n    seamless && \"input-group-seamless\",\n    size && `input-group-${size}`\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nInputGroup.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * Whether it is seamless, or not.\n   */\n  seamless: PropTypes.bool,\n  /**\n   * The tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nInputGroup.defaultProps = {\n  tag: \"div\"\n};\n\nexport default InputGroup;\n",
    "examples": [{
        "name": "InputGroup",
        "filename": "01-basic-input-group.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Input Groups</h2>\n"
        },
        "code": "import React from \"react\";\nimport {\n  InputGroup,\n  InputGroupText,\n  InputGroupAddon,\n  FormInput,\n  Button\n} from \"shards-react\";\n\n/**\n * ## Basic Input Groups\n */\nexport default function FormSelectDemo() {\n  return (\n    <div>\n      <InputGroup className=\"mb-2\">\n        <InputGroupAddon type=\"prepend\">\n          <InputGroupText>$</InputGroupText>\n        </InputGroupAddon>\n        <FormInput placeholder=\"Total Amount\" />\n      </InputGroup>\n\n      <InputGroup className=\"mb-2\">\n        <FormInput placeholder=\"Total Amount\" />\n        <InputGroupAddon type=\"append\">\n          <InputGroupText>$</InputGroupText>\n        </InputGroupAddon>\n      </InputGroup>\n\n      <InputGroup>\n        <InputGroupAddon type=\"prepend\">\n          <InputGroupText>designrevision.com/user/</InputGroupText>\n        </InputGroupAddon>\n        <FormInput placeholder=\"username\" />\n        <InputGroupAddon type=\"append\">\n          <Button theme=\"secondary\">Check</Button>\n        </InputGroupAddon>\n      </InputGroup>\n    </div>\n  );\n}\n"
    }, {
        "name": "InputGroup",
        "filename": "02-input-group-addons.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Input Group Addons</h2>\n<p>Input group addons can be created using the <code>&#x3C;InputGroupAddon></code> component. The type of the addon component can be also controlled via the <code>type</code> prop using either <code>append</code> or <code>prepend</code> as values.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  InputGroup,\n  InputGroupText,\n  InputGroupAddon,\n  FormInput\n} from \"shards-react\";\n\n/**\n * ## Input Group Addons\n *\n * Input group addons can be created using the `<InputGroupAddon>` component. The type of the addon component can be also controlled via the `type` prop using either `append` or `prepend` as values.\n */\nexport default function InputGroupAddonExample() {\n  return (\n    <InputGroup>\n      <InputGroupAddon type=\"prepend\">\n        <InputGroupText>Total Amount</InputGroupText>\n      </InputGroupAddon>\n      <FormInput />\n      <InputGroupAddon type=\"append\">\n        <InputGroupText>.00 (USD)</InputGroupText>\n      </InputGroupAddon>\n    </InputGroup>\n  );\n}\n"
    }, {
        "name": "InputGroup",
        "filename": "03-input-group-dropdowns.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Input Group Dropdowns</h2>\n<p>You can create dropdowns inside input groups via the <code>addonType</code> prop on the <code>Dropdown</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  InputGroup,\n  FormInput,\n  Dropdown,\n  DropdownItem,\n  DropdownToggle,\n  DropdownMenu\n} from \"shards-react\";\n\n/**\n * ## Input Group Dropdowns\n *\n * You can create dropdowns inside input groups via the `addonType` prop on the `Dropdown` component.\n */\nexport default class InputGroupDropdownExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = {\n      open: false\n    };\n  }\n\n  toggle() {\n    this.setState({ open: !this.state.open });\n  }\n\n  render() {\n    return (\n      <InputGroup>\n        <FormInput />\n        <Dropdown\n          addonType=\"append\"\n          open={this.state.open}\n          toggle={this.toggle}\n        >\n          <DropdownToggle caret>Dropdown</DropdownToggle>\n          <DropdownMenu right>\n            <DropdownItem>Action</DropdownItem>\n            <DropdownItem>Another action</DropdownItem>\n            <DropdownItem>Something else here</DropdownItem>\n          </DropdownMenu>\n        </Dropdown>\n      </InputGroup>\n    );\n  }\n}\n"
    }, {
        "name": "InputGroup",
        "filename": "04-input-group-sizing.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Sizing</h2>\n<p>You can control the size of your input group via the <code>size</code> prop. Available options are <code>sm</code> for small and <code>lg</code> for large.</p>\n"
        },
        "code": "import React from \"react\";\nimport {\n  InputGroup,\n  InputGroupText,\n  InputGroupAddon,\n  FormInput\n} from \"shards-react\";\n\n/**\n * ## Sizing\n *\n * You can control the size of your input group via the `size` prop. Available options are `sm` for small and `lg` for large.\n */\nexport default function InputGroupAddonExample() {\n  return (\n    <div>\n      <InputGroup size=\"sm\" className=\"mb-2\">\n        <InputGroupAddon type=\"prepend\">\n          <InputGroupText>Total Amount</InputGroupText>\n        </InputGroupAddon>\n        <FormInput />\n        <InputGroupAddon type=\"append\">\n          <InputGroupText>$ (USD)</InputGroupText>\n        </InputGroupAddon>\n      </InputGroup>\n\n      <InputGroup className=\"mb-2\">\n        <InputGroupAddon type=\"prepend\">\n          <InputGroupText>Total Amount</InputGroupText>\n        </InputGroupAddon>\n        <FormInput />\n        <InputGroupAddon type=\"append\">\n          <InputGroupText>$ (USD)</InputGroupText>\n        </InputGroupAddon>\n      </InputGroup>\n\n      <InputGroup size=\"lg\">\n        <InputGroupAddon type=\"prepend\">\n          <InputGroupText>Total Amount</InputGroupText>\n        </InputGroupAddon>\n        <FormInput />\n        <InputGroupAddon type=\"append\">\n          <InputGroupText>$ (USD)</InputGroupText>\n        </InputGroupAddon>\n      </InputGroup>\n    </div>\n  );\n}\n"
    }, {
        "name": "InputGroup",
        "filename": "05-seamless-input-groups.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Seamless Input Groups</h2>\n<p>You can create seamless input groups using the <code>seamless</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { FontAwesomeIcon } from \"@fortawesome/react-fontawesome\";\nimport {\n  InputGroup,\n  InputGroupText,\n  InputGroupAddon,\n  FormInput\n} from \"shards-react\";\n\n/**\n * ## Seamless Input Groups\n *\n * You can create seamless input groups using the `seamless` prop.\n */\nexport default function InputGroupAddonExample() {\n  return (\n    <InputGroup seamless>\n      <InputGroupAddon type=\"prepend\">\n        <InputGroupText>\n          <FontAwesomeIcon icon={[\"fab\", \"twitter\"]} />\n        </InputGroupText>\n      </InputGroupAddon>\n      <FormInput />\n    </InputGroup>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "InputGroupAddon",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "children": {
                "type": {
                    "name": "node"
                },
                "required": false,
                "description": "The children nodes."
            },
            "type": {
                "type": {
                    "name": "enum",
                    "computed": true,
                    "value": "INPUT_GROUP_ADDON_TYPES"
                },
                "required": true,
                "description": "The addon type."
            },
            "tag": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "InputGroupText",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"span\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Using the <code>InputGroup</code> component you can easily extend form controls by adding various elements such as text, buttons and button groups.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "seamless": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is seamless, or not."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/list-group",
    "name": "ListGroup",
    "filename": "ListGroup.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * List groups allow you to display series of content.\n */\nconst ListGroup = props => {\n  const { className, tag: Tag, flush, small, ...attrs } = props;\n  const classes = classNames(\n    className,\n    \"list-group\",\n    small && \"list-group-sm\",\n    flush && \"list-group-flush\"\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nListGroup.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * Whether the list group should be flushed, or not.\n   */\n  flush: PropTypes.bool,\n  /**\n   * Whether the list group is small, or not.\n   */\n  small: PropTypes.bool,\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nListGroup.defaultProps = {\n  tag: \"ul\"\n};\n\nexport default ListGroup;\n",
    "examples": [{
        "name": "ListGroup",
        "filename": "01-basic-list-group.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>You can create list groups using the <code>ListGroup</code> component with the <code>ListGroupItem</code> sub-component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { ListGroup, ListGroupItem } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * You can create list groups using the `ListGroup` component with the `ListGroupItem` sub-component.\n */\nexport default function ListGroupDemo() {\n  return (\n    <ListGroup>\n      <ListGroupItem>Cras justo odio</ListGroupItem>\n      <ListGroupItem>Dapibus ac facilisis in</ListGroupItem>\n      <ListGroupItem>Morbi leo risus</ListGroupItem>\n      <ListGroupItem>Porta ac consectetur ac</ListGroupItem>\n      <ListGroupItem>Vestibulum at eros</ListGroupItem>\n    </ListGroup>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ListGroupItem",
        "methods": [],
        "props": {
            "active": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is active, or not."
            },
            "disabled": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is disabled, or not."
            },
            "theme": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The theme color."
            },
            "action": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is an action item, or not."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"li\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ListGroupItemHeading",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "any"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"h5\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ListGroupItemText",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"p\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>List groups allow you to display series of content.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "flush": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the list group should be flushed, or not."
        },
        "small": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the list group is small, or not."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"ul\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/modal",
    "name": "Modal",
    "filename": "Modal.js",
    "code": "import React, { Fragment } from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport { Transition } from \"react-transition-group\";\n\nimport { TIMEOUT } from \"../constants\";\n\n/**\n * Creating flexible modal dialogs can be achieved using the `Modal` component. They feature a series of helpful subcomponents, sizes and various other options that you can use to customize the display and behavior of your modals.\n */\nclass Modal extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.state = {\n      open: this.props.open || false\n    };\n\n    this.handleOnEntered = this.handleOnEntered.bind(this);\n    this.handleOnExit = this.handleOnExit.bind(this);\n    this.handleOnExited = this.handleOnExited.bind(this);\n    this.handleBackdropClick = this.handleBackdropClick.bind(this);\n\n    this.modalContent = null;\n  }\n\n  componentDidUpdate(prevProps, prevState) {\n    if (prevState.open !== this.props.open) {\n      this.setState({ open: this.props.open });\n    }\n  }\n\n  handleOnEntered(type, node) {\n    const { fade, showModal } = this.props;\n\n    if (type === \"backdrop\" && fade === false) {\n      return;\n    }\n\n    node.classList.add(\"show\");\n\n    if (type === \"modal\") {\n      showModal && showModal();\n    }\n  }\n\n  handleOnExit(type, node) {\n    const { fade, hideModal } = this.props;\n\n    if (type === \"backdrop\" && fade === false) {\n      return;\n    }\n\n    node.classList.remove(\"show\");\n\n    if (type === \"modal\") {\n      hideModal && hideModal();\n    }\n  }\n\n  handleOnExited() {\n    this.props.hiddenModal && this.props.hiddenModal();\n  }\n\n  handleBackdropClick(e) {\n    if (!this.modalContent.contains(e.target)) {\n      this.props.toggle();\n    }\n  }\n\n  render() {\n    if (!this.state.open) {\n      return null;\n    }\n\n    const {\n      id,\n      backdrop,\n      fade,\n      tabIndex,\n      backdropClassName,\n      modalClassName,\n      animation,\n      modalContentClassName,\n      position,\n      role,\n      size,\n      children,\n      centered,\n      className\n    } = this.props; // open, showModal, hideModal, hiddenModal, toggle\n\n    const backdropClasses = classNames(\n      \"modal-backdrop\",\n      fade ? \"fade\" : \"show\",\n      backdropClassName\n    );\n\n    const modalClasses = classNames(\n      \"modal\",\n      fade && \"fade\",\n      modalClassName,\n      fade &&\n        (animation || (position && position.split(\"-\").slice(-1)[0]) || \"top\")\n    );\n\n    const modalAttrs = {\n      \"aria-hidden\": true,\n      id: id || undefined,\n      tabIndex,\n      role,\n      style: {\n        display: \"block\"\n      }\n    };\n\n    const modalDialogClasses = classNames(\n      \"modal-dialog\",\n      className,\n      size && `modal-${size}`,\n      centered && \"modal-dialog-centered\",\n      position && `modal-${position}`\n    );\n\n    const contentClasses = classNames(\"modal-content\", modalContentClassName);\n\n    return (\n      <Fragment>\n        {backdrop && (\n          <Transition\n            timeout={fade ? TIMEOUT.FADE : 0}\n            in={this.state.open}\n            appear={this.state.open}\n            mountOnEnter\n            unmountOnExit\n            onEntered={node => this.handleOnEntered(\"backdrop\", node)}\n            onExit={node => this.handleOnExit(\"backdrop\", node)}\n            onExited={this.handleOnExited}\n          >\n            <div className={backdropClasses} />\n          </Transition>\n        )}\n        <Transition\n          timeout={fade ? TIMEOUT.FADE : 0}\n          in={this.state.open}\n          appear={this.state.open}\n          mountOnEnter\n          unmountOnExit\n          onClick={this.handleBackdropClick}\n          onEntered={node => this.handleOnEntered(\"modal\", node)}\n          onExit={node => this.handleOnExit(\"modal\", node)}\n        >\n          <div className={modalClasses} {...modalAttrs}>\n            <div className={modalDialogClasses} role=\"document\">\n              <div\n                ref={el => (this.modalContent = el)}\n                className={contentClasses}\n              >\n                {children}\n              </div>\n            </div>\n          </div>\n        </Transition>\n      </Fragment>\n    );\n  }\n}\n\nModal.propTypes = {\n  /**\n   * The id.\n   */\n  id: PropTypes.string,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * Whether it is open, or not.\n   */\n  open: PropTypes.bool,\n  /**\n   * Whether it should fade, or not.\n   */\n  fade: PropTypes.bool,\n  /**\n   * Whether it should display a backdrop, or not.\n   */\n  backdrop: PropTypes.bool,\n  /**\n   * The function that should be triggered when the modal is shown.\n   */\n  showModal: PropTypes.func,\n  /**\n   * The function that should be triggered when the modal is set to hide.\n   */\n  hideModal: PropTypes.func,\n  /**\n   * The function that should be triggered when the modal is finally hidden.\n   */\n  hiddenModal: PropTypes.func,\n  /**\n   * Whether it should be centered, or not.\n   */\n  centered: PropTypes.bool,\n  /**\n   * The class name for the backdrop element.\n   */\n  backdropClassName: PropTypes.string,\n  /**\n   * The toggle function.\n   */\n  toggle: PropTypes.func,\n  /**\n   * The class name for the modal.\n   */\n  modalClassName: PropTypes.string,\n  /**\n   *\n   */\n  animation: PropTypes.bool,\n  /**\n   * The position.\n   */\n  position: PropTypes.string,\n  /**\n   * The size.\n   */\n  size: PropTypes.string,\n  /**\n   * The tab index.\n   */\n  tabIndex: PropTypes.string,\n  /**\n   * The class name for the modal content.\n   */\n  modalContentClassName: PropTypes.string,\n  /**\n   * The role attribute for the modal.\n   */\n  role: PropTypes.string,\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.node),\n    PropTypes.node\n  ])\n};\n\nModal.defaultProps = {\n  open: false,\n  fade: true,\n  backdrop: true,\n  role: \"dialog\"\n};\n\nexport default Modal;\n",
    "examples": [{
        "name": "Modal",
        "filename": "01-basic-modal.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "code": "import React from \"react\";\nimport { Button, Modal, ModalBody, ModalHeader } from \"shards-react\";\n\nexport default class BasicModalExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.state = { open: false };\n    this.toggle = this.toggle.bind(this);\n  }\n\n  toggle() {\n    this.setState({\n      open: !this.state.open\n    });\n  }\n\n  render() {\n    const { open } = this.state;\n    return (\n      <div>\n        <Button onClick={this.toggle}>Click Me!</Button>\n        <Modal open={open} toggle={this.toggle}>\n          <ModalHeader>Header</ModalHeader>\n          <ModalBody>👋 Hello there!</ModalBody>\n        </Modal>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Modal",
        "filename": "02-modal-size.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Modal Size</h2>\n<p>The modal's size can be adjusted via the <code>size</code> prop. You can use <code>sm</code> for small and <code>lg</code> for large.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, Modal, ModalBody, ModalHeader } from \"shards-react\";\n\n/**\n * ## Modal Size\n *\n * The modal's size can be adjusted via the `size` prop. You can use `sm` for small and `lg` for large.\n */\nexport default class ModalSizeExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.state = { open: false };\n    this.toggle = this.toggle.bind(this);\n  }\n\n  toggle() {\n    this.setState({\n      open: !this.state.open\n    });\n  }\n\n  render() {\n    const { open } = this.state;\n    return (\n      <div>\n        <Button onClick={this.toggle}>Small Modal!</Button>\n        <Modal size=\"sm\" open={open} toggle={this.toggle}>\n          <ModalHeader>Header</ModalHeader>\n          <ModalBody>👋 Hello there!</ModalBody>\n        </Modal>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ModalBody",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "children": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "arrayOf",
                        "value": {
                            "name": "node"
                        }
                    }, {
                        "name": "node"
                    }]
                },
                "required": false,
                "description": "The children nodes."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ModalFooter",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "children": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "arrayOf",
                        "value": {
                            "name": "node"
                        }
                    }, {
                        "name": "node"
                    }]
                },
                "required": false,
                "description": "The children nodes."
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "ModalHeader",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "toggle": {
                "type": {
                    "name": "func"
                },
                "required": false,
                "description": "The toggle function."
            },
            "tag": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"h5\"",
                    "computed": false
                }
            },
            "closeAriaLabel": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The close button's aria label.",
                "defaultValue": {
                    "value": "\"Close\"",
                    "computed": false
                }
            },
            "titleClass": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class for the modal title."
            },
            "children": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "arrayOf",
                        "value": {
                            "name": "node"
                        }
                    }, {
                        "name": "node"
                    }]
                },
                "required": false,
                "description": "The children nodes."
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Creating flexible modal dialogs can be achieved using the <code>Modal</code> component. They feature a series of helpful subcomponents, sizes and various other options that you can use to customize the display and behavior of your modals.</p>\n"
    },
    "props": {
        "id": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The id."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is open, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "fade": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should fade, or not.",
            "defaultValue": {
                "value": "true",
                "computed": false
            }
        },
        "backdrop": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should display a backdrop, or not.",
            "defaultValue": {
                "value": "true",
                "computed": false
            }
        },
        "showModal": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The function that should be triggered when the modal is shown."
        },
        "hideModal": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The function that should be triggered when the modal is set to hide."
        },
        "hiddenModal": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The function that should be triggered when the modal is finally hidden."
        },
        "centered": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be centered, or not."
        },
        "backdropClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name for the backdrop element."
        },
        "toggle": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The toggle function."
        },
        "modalClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name for the modal."
        },
        "animation": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": ""
        },
        "position": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The position."
        },
        "size": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The size."
        },
        "tabIndex": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The tab index."
        },
        "modalContentClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name for the modal content."
        },
        "role": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The role attribute for the modal.",
            "defaultValue": {
                "value": "\"dialog\"",
                "computed": false
            }
        },
        "children": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "node"
                    }
                }, {
                    "name": "node"
                }]
            },
            "required": false,
            "description": "The children nodes."
        }
    }
}, {
    "slug": "component/nav",
    "name": "Nav",
    "filename": "Nav.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * The `Nav` component allows you to build all types of navigation components.\n */\nconst Nav = props => {\n  const {\n    className,\n    navbar,\n    horizontal,\n    vertical,\n    tabs,\n    card,\n    pills,\n    justified,\n    fill,\n    tag: Tag,\n    ...attrs\n  } = props;\n\n  let verticalClass;\n\n  if (vertical === true || vertical === \"xs\") {\n    verticalClass = \"flex-column\";\n  } else if (vertical === false) {\n    verticalClass = false;\n  } else if (typeof vertical === \"string\") {\n    verticalClass = `flex-${vertical}-column`;\n  }\n\n  const classes = classNames(\n    className,\n    navbar ? \"navbar-nav\" : \"nav\",\n    horizontal && `justify-content-${horizontal}`,\n    verticalClass,\n    tabs && \"nav-tabs\",\n    card && tabs && \"card-header-tabs\",\n    pills && \"nav-pills\",\n    card && pills && \"card-header-pills\",\n    justified && \"nav-justified\",\n    fill && \"nav-fill\"\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nNav.defaultProps = {\n  tag: \"ul\",\n  vertical: false\n};\n\nNav.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * Whether it is located inside a Navbar, or not.\n   */\n  navbar: PropTypes.bool,\n  /**\n   * Justify content horizontally.\n   */\n  horizontal: PropTypes.string,\n  /**\n   * Whether it should be displayed as tabs, or not.\n   */\n  tabs: PropTypes.bool,\n  /**\n   * Whether it is located inside a card, or not.\n   */\n  card: PropTypes.bool,\n  /**\n   * Whether it should be displayed as pills, or not.\n   */\n  pills: PropTypes.bool,\n  /**\n   * Whether it is justified, or not.\n   */\n  justified: PropTypes.bool,\n  /**\n   * Whether it should fill the entire space, or not.\n   */\n  fill: PropTypes.bool,\n  /**\n   * Whether it is vertical, or not.\n   */\n  vertical: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string])\n};\n\nexport default Nav;\n",
    "examples": [{
        "name": "Nav",
        "filename": "01-basic-nav.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Demo</h2>\n<p>Creating navs can be easily achieved using the <code>Nav</code> component together with the <code>NavItem</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Nav, NavItem, NavLink } from \"shards-react\";\n\n/**\n * ## Basic Demo\n *\n * Creating navs can be easily achieved using the `Nav` component together with the `NavItem` component.\n */\nexport default function NavExample() {\n  return (\n    <Nav>\n      <NavItem>\n        <NavLink active href=\"#\">\n          Active\n        </NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Another Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink disabled href=\"#\">\n          Disabled Link\n        </NavLink>\n      </NavItem>\n    </Nav>\n  );\n}\n"
    }, {
        "name": "Nav",
        "filename": "02-nav-tabs-style.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Tabs Style</h2>\n<p>Using the <code>tabs</code> prop, you can style the navs to look like tabs.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Nav, NavItem, NavLink } from \"shards-react\";\n\n/**\n * ## Tabs Style\n *\n * Using the `tabs` prop, you can style the navs to look like tabs.\n */\nexport default function NavTabsExample() {\n  return (\n    <Nav tabs>\n      <NavItem>\n        <NavLink active href=\"#\">\n          Active\n        </NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Another Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink disabled href=\"#\">\n          Disabled Link\n        </NavLink>\n      </NavItem>\n    </Nav>\n  );\n}\n"
    }, {
        "name": "Nav",
        "filename": "03-nav-pills-style.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Pills Style</h2>\n<p>Using the <code>pills</code> prop you can style the navs to look like pills.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Nav, NavItem, NavLink } from \"shards-react\";\n\n/**\n * ## Pills Style\n *\n * Using the `pills` prop you can style the navs to look like pills.\n */\nexport default function NavPillsExample() {\n  return (\n    <Nav pills>\n      <NavItem>\n        <NavLink active href=\"#\">\n          Active\n        </NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Another Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink disabled href=\"#\">\n          Disabled Link\n        </NavLink>\n      </NavItem>\n    </Nav>\n  );\n}\n"
    }, {
        "name": "Nav",
        "filename": "04-nav-fill-style.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Fill Style</h2>\n<p>Using the <code>fill</code> prop, you can force the nav content to extend the full available width.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Nav, NavItem, NavLink } from \"shards-react\";\n\n/**\n * ## Fill Style\n *\n * Using the `fill` prop, you can force the nav content to extend the full available width.\n */\nexport default function NavPillsExample() {\n  return (\n    <Nav fill>\n      <NavItem>\n        <NavLink href=\"#\">Active</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Another Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink disabled href=\"#\">\n          Disabled Link\n        </NavLink>\n      </NavItem>\n    </Nav>\n  );\n}\n"
    }, {
        "name": "Nav",
        "filename": "05-nav-justified-style.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Justified</h2>\n<p>Using the <code>justified</code> prop, you can define equal-width nav elements.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Nav, NavItem, NavLink } from \"shards-react\";\n\n/**\n * ## Justified\n *\n * Using the `justified` prop, you can define equal-width nav elements.\n */\nexport default function NavPillsExample() {\n  return (\n    <Nav justified>\n      <NavItem>\n        <NavLink href=\"#\">Active</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink href=\"#\">Another Link</NavLink>\n      </NavItem>\n      <NavItem>\n        <NavLink disabled href=\"#\">\n          Disabled Link\n        </NavLink>\n      </NavItem>\n    </Nav>\n  );\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "NavItem",
        "methods": [],
        "props": {
            "active": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is active, or not."
            },
            "disabled": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is disabled, or not."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"li\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "NavLink",
        "methods": [{
            "name": "handleOnClick",
            "docblock": null,
            "modifiers": [],
            "params": [{
                "name": "e",
                "type": null
            }],
            "returns": null
        }],
        "props": {
            "disabled": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is disabled, or not."
            },
            "active": {
                "type": {
                    "name": "bool"
                },
                "required": false,
                "description": "Whether it is active, or not."
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "onClick": {
                "type": {
                    "name": "func"
                },
                "required": false,
                "description": "The function that should be triggered on click."
            },
            "href": {
                "type": {
                    "name": "any"
                },
                "required": false,
                "description": "The href attribute value."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"a\"",
                    "computed": false
                }
            },
            "innerRef": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "object"
                    }, {
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The inner ref."
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The <code>Nav</code> component allows you to build all types of navigation components.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "navbar": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is located inside a Navbar, or not."
        },
        "horizontal": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "Justify content horizontally."
        },
        "tabs": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be displayed as tabs, or not."
        },
        "card": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is located inside a card, or not."
        },
        "pills": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should be displayed as pills, or not."
        },
        "justified": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is justified, or not."
        },
        "fill": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it should fill the entire space, or not."
        },
        "vertical": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "bool"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "Whether it is vertical, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"ul\"",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/navbar",
    "name": "Navbar",
    "filename": "Navbar.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\n/**\n * Using the `Navbar` component you can create powerful and responsive navigation headers.\n */\nconst Navbar = props => {\n  const {\n    className,\n    expand,\n    fixed,\n    sticky,\n    theme,\n    type,\n    tag: Tag,\n    ...attrs\n  } = props;\n\n  let expandClass;\n\n  if (expand === false) {\n    expandClass = false;\n  } else if (expand === true || expand === \"xs\") {\n    expandClass = \"navbar-expand\";\n  } else if (typeof expand === \"string\") {\n    expandClass = `navbar-expand-${expand}`;\n  }\n\n  const classes = classNames(\n    className,\n    \"navbar\",\n    expandClass,\n    type === \"light\" && \"navbar-light\",\n    type === \"dark\" && \"navbar-dark\",\n    theme && `bg-${theme}`,\n    fixed && `fixed-${fixed}`,\n    sticky && `sticky-${sticky}`\n  );\n\n  return <Tag {...attrs} className={classes} />;\n};\n\nNavbar.propTypes = {\n  /**\n   * Whether it is full, or not.\n   */\n  full: PropTypes.bool,\n  /**\n   * Whether it is fixed, or not.\n   */\n  fixed: PropTypes.string,\n  /**\n   * Whether it is sticky, or not.\n   */\n  sticky: PropTypes.string,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * The role attribute.\n   */\n  role: PropTypes.string,\n  /**\n   * The class name attribute.\n   */\n  className: PropTypes.string,\n  /**\n   * The navbar type.\n   */\n  type: PropTypes.oneOf([\"dark\", \"light\"]),\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),\n  /**\n   * Whether it should expand, or not.\n   */\n  expand: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])\n};\n\nNavbar.defaultProps = {\n  tag: \"nav\",\n  expand: false\n};\n\nexport default Navbar;\n",
    "examples": [{
        "name": "Navbar",
        "filename": "01-basic-navbar.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n"
        },
        "code": "import React from \"react\";\nimport { faSearch } from \"@fortawesome/free-solid-svg-icons\";\nimport { FontAwesomeIcon } from \"@fortawesome/react-fontawesome\";\nimport {\n  Navbar,\n  NavbarToggler,\n  NavbarBrand,\n  Nav,\n  NavItem,\n  NavLink,\n  Dropdown,\n  DropdownToggle,\n  DropdownMenu,\n  DropdownItem,\n  InputGroup,\n  InputGroupAddon,\n  InputGroupText,\n  FormInput,\n  Collapse\n} from \"shards-react\";\n\n/**\n * ## Basic Example\n */\nexport default class NavExample extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.toggleDropdown = this.toggleDropdown.bind(this);\n    this.toggleNavbar = this.toggleNavbar.bind(this);\n\n    this.state = {\n      dropdownOpen: false,\n      collapseOpen: false\n    };\n  }\n\n  toggleDropdown() {\n    this.setState({\n      ...this.state,\n      ...{\n        dropdownOpen: !this.state.dropdownOpen\n      }\n    });\n  }\n\n  toggleNavbar() {\n    this.setState({\n      ...this.state,\n      ...{\n        collapseOpen: !this.state.collapseOpen\n      }\n    });\n  }\n\n  render() {\n    return (\n      <Navbar type=\"dark\" theme=\"primary\" expand=\"md\">\n        <NavbarBrand href=\"#\">Shards React</NavbarBrand>\n        <NavbarToggler onClick={this.toggleNavbar} />\n\n        <Collapse open={this.state.collapseOpen} navbar>\n          <Nav navbar>\n            <NavItem>\n              <NavLink active href=\"#\">\n                Active\n              </NavLink>\n            </NavItem>\n            <NavItem>\n              <NavLink href=\"#\" disabled>\n                Disabled\n              </NavLink>\n            </NavItem>\n            <Dropdown\n              open={this.state.dropdownOpen}\n              toggle={this.toggleDropdown}\n            >\n              <DropdownToggle nav caret>\n                Dropdown\n              </DropdownToggle>\n              <DropdownMenu>\n                <DropdownItem>Action</DropdownItem>\n                <DropdownItem>Another action</DropdownItem>\n                <DropdownItem>Something else here</DropdownItem>\n              </DropdownMenu>\n            </Dropdown>\n          </Nav>\n\n          <Nav navbar className=\"ml-auto\">\n            <InputGroup size=\"sm\" seamless>\n              <InputGroupAddon type=\"prepend\">\n                <InputGroupText>\n                  <FontAwesomeIcon icon={faSearch} />\n                </InputGroupText>\n              </InputGroupAddon>\n              <FormInput className=\"border-0\" placeholder=\"Search...\" />\n            </InputGroup>\n          </Nav>\n        </Collapse>\n      </Navbar>\n    );\n  }\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "NavbarBrand",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"a\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "NavbarToggler",
        "methods": [],
        "props": {
            "type": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"button\"",
                    "computed": false
                }
            },
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "children": {
                "type": {
                    "name": "node"
                },
                "required": false,
                "description": "The children nodes."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The tag type.",
                "defaultValue": {
                    "value": "\"button\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Using the <code>Navbar</code> component you can create powerful and responsive navigation headers.</p>\n"
    },
    "props": {
        "full": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is full, or not."
        },
        "fixed": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "Whether it is fixed, or not."
        },
        "sticky": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "Whether it is sticky, or not."
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color."
        },
        "role": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The role attribute."
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name attribute."
        },
        "type": {
            "type": {
                "name": "enum",
                "value": [{
                    "value": "\"dark\"",
                    "computed": false
                }, {
                    "value": "\"light\"",
                    "computed": false
                }]
            },
            "required": false,
            "description": "The navbar type."
        },
        "tag": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "func"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"nav\"",
                "computed": false
            }
        },
        "expand": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "bool"
                }, {
                    "name": "string"
                }]
            },
            "required": false,
            "description": "Whether it should expand, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/popover",
    "name": "Popover",
    "filename": "Popover.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\n\nimport PopperManager from \"../../utils/PopperManager\";\nimport { CustomPropTypes, getTarget } from \"../utils\";\nimport { EVENTS, TIMEOUT } from \"../constants\";\n\n/**\n * Popovers are powerful elements similar to tooltips and powered by Popper.js that can be applies to any interactive element.\n */\nclass Popover extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.show = this.show.bind(this);\n    this.hide = this.hide.bind(this);\n    this.maybeShow = this.maybeShow.bind(this);\n    this.toggle = this.toggle.bind(this);\n    this.addListeners = this.addListeners.bind(this);\n    this.removeListeners = this.removeListeners.bind(this);\n    this.handleClick = this.handleClick.bind(this);\n    this.getDelay = this.getDelay.bind(this);\n\n    this._target = null;\n    this._hideTimeout = null;\n    this._showTimeout = null;\n  }\n\n  componentDidMount() {\n    this._target = getTarget(this.props.target);\n    this.maybeShow();\n  }\n\n  componentDidUpdate() {\n    this.maybeShow();\n  }\n\n  componentWillUnmount() {\n    clearTimeout(this._showTimeout);\n    clearTimeout(this._hideTimeout);\n    this.removeListeners();\n  }\n\n  show() {\n    clearTimeout(this._hideTimeout);\n    this.addListeners();\n\n    if (!this.props.open) {\n      clearTimeout(this._showTimeout);\n      this._showTimeout = setTimeout(this.toggle, this.getDelay(\"show\"));\n    }\n  }\n\n  hide() {\n    clearTimeout(this._showTimeout);\n    this.removeListeners();\n\n    if (this.props.open) {\n      clearTimeout(this._hideTimeout);\n      this._hideTimeout = setTimeout(this.toggle, this.getDelay(\"hide\"));\n    }\n  }\n\n  maybeShow() {\n    if (this.props.open) {\n      this.show();\n      return;\n    }\n\n    this.hide();\n  }\n\n  toggle(event) {\n    if (this.props.disabled) {\n      event.preventDefault();\n      return;\n    }\n\n    return this.props.toggle(event);\n  }\n\n  addListeners() {\n    EVENTS.CLICK.forEach(event =>\n      document.addEventListener(event, this.handleClick, true)\n    );\n  }\n\n  removeListeners() {\n    EVENTS.CLICK.forEach(event => {\n      document.removeEventListener(event, this.handleClick, true);\n    });\n  }\n\n  handleClick(event) {\n    if (!this._target) {\n      return;\n    }\n\n    if (\n      event.target !== this._target &&\n      !this._target.contains(event.target) &&\n      event.target !== this._popover &&\n      !(this._popover && this._popover.contains(event.target))\n    ) {\n      if (this._hideTimeout) {\n        clearTimeout(this._hideTimeout);\n      }\n\n      if (this.props.open) {\n        this.toggle(event);\n      }\n    }\n  }\n\n  getDelay(key) {\n    key = key.toUpperCase();\n    if (typeof this.props.delay === \"object\") {\n      return isNaN(this.props.delay[key])\n        ? TIMEOUT[key]\n        : this.props.delay[key];\n    }\n\n    return this.props.delay;\n  }\n\n  render() {\n    const {\n      className,\n      target,\n      container,\n      modifiers,\n      open,\n      innerClassName,\n      noArrow,\n      arrowClassName,\n      placement,\n      placementPrefix,\n      boundariesElement,\n      offset,\n      ...attrs\n    } = this.props; // disabled, toggle, delay\n\n    if (!open) {\n      return null;\n    }\n\n    const classes = classNames(\"popover-inner\", innerClassName);\n    const popperClasses = classNames(\"popover\", \"show\", className);\n\n    return (\n      <PopperManager\n        className={popperClasses}\n        target={target}\n        container={container}\n        modifiers={modifiers}\n        offset={offset}\n        open={open}\n        noArrow={noArrow}\n        arrowClassName={arrowClassName}\n        placement={placement}\n        placementPrefix={placementPrefix}\n        boundariesElement={boundariesElement}\n      >\n        <div {...attrs} className={classes} />\n      </PopperManager>\n    );\n  }\n}\n\nPopover.propTypes = {\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n\n  /**\n   * The target element.\n   */\n  target: CustomPropTypes.target.isRequired,\n\n  /**\n   * The popover container.\n   */\n  container: CustomPropTypes.target,\n\n  /**\n   * Popper modifiers object.\n   */\n  modifiers: PropTypes.object,\n\n  /**\n   * Whether the popover is open, or not.\n   */\n  open: PropTypes.bool,\n\n  /**\n   * The inner class name.\n   */\n  innerClassName: PropTypes.string,\n\n  /**\n   * Whether the popover is disabled, or not.\n   */\n  disabled: PropTypes.bool,\n\n  /**\n   * Whether to hide the arrow, or not.\n   */\n  noArrow: PropTypes.bool,\n\n  /**\n   * The arrow class name.\n   */\n  arrowClassName: PropTypes.string,\n\n  /**\n   * The boundaries element for the Popover instance.\n   */\n  boundariesElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),\n\n  /**\n   * The popover placement.\n   */\n  placement: PropTypes.string,\n\n  /**\n   * The placement prefix.\n   */\n  placementPrefix: PropTypes.string,\n\n  /**\n   * The popover offset.\n   */\n  offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),\n\n  /**\n   * The toggle function.\n   */\n  toggle: PropTypes.func.isRequired,\n\n  /**\n   * The show/hide delay in ms.\n   */\n  delay: PropTypes.oneOfType([\n    PropTypes.number,\n    PropTypes.shape({\n      show: PropTypes.number,\n      hide: PropTypes.number\n    })\n  ])\n};\n\nPopover.defaultProps = {\n  open: false,\n  noArrow: false,\n  placement: \"top\",\n  placementPrefix: \"bs-popover\",\n  delay: {\n    show: 0,\n    hide: 0\n  },\n  toggle: function() {}\n};\n\nexport default Popover;\n",
    "examples": [{
        "name": "Popover",
        "filename": "01-basic-popover.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>Popovers can be created using the <code>Popover</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, Popover, PopoverBody, PopoverHeader } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * Popovers can be created using the `Popover` component.\n */\nexport default class PopoverExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = {\n      open: false\n    };\n  }\n\n  toggle() {\n    this.setState({\n      open: !this.state.open\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button id=\"popover-1\" onClick={this.toggle}>\n          Toggle\n        </Button>\n        <Popover\n          placement=\"bottom\"\n          open={this.state.open}\n          toggle={this.toggle}\n          target=\"#popover-1\"\n        >\n          <PopoverHeader>Title here</PopoverHeader>\n          <PopoverBody>\n            Anim pariatur cliche reprehenderit, enim eiusmod high life accusamus\n            terry richardson ad squid. Nihil anim keffiyeh helvetica, craft beer\n            labore wes anderson cred nesciunt sapiente ea proident.\n          </PopoverBody>\n        </Popover>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Popover",
        "filename": "02-popover-placement.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Placement</h2>\n<p>Using the <code>placement</code> prop you can adjust where your popover will be displayed.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Button, Popover, PopoverBody, PopoverHeader } from \"shards-react\";\n\n/**\n * ## Placement\n *\n * Using the `placement` prop you can adjust where your popover will be displayed.\n */\nexport default class PopoverPlacementExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState({ open: !this.state.open });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button id=\"popover-2\" onClick={this.toggle}>\n          Toggle\n        </Button>\n        <Popover\n          placement=\"left\"\n          open={this.state.open}\n          toggle={this.toggle}\n          target=\"#popover-2\"\n        >\n          <PopoverHeader>Title here</PopoverHeader>\n          <PopoverBody>\n            Anim pariatur cliche reprehenderit, enim eiusmod high life accusamus\n            terry richardson ad squid. Nihil anim keffiyeh helvetica, craft beer\n            labore wes anderson cred nesciunt sapiente ea proident.\n          </PopoverBody>\n        </Popover>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [{
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "PopoverBody",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The component's tag type.",
                "defaultValue": {
                    "value": "\"div\"",
                    "computed": false
                }
            }
        }
    }, {
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "\n"
        },
        "displayName": "PopoverHeader",
        "methods": [],
        "props": {
            "className": {
                "type": {
                    "name": "string"
                },
                "required": false,
                "description": "The class name."
            },
            "tag": {
                "type": {
                    "name": "union",
                    "value": [{
                        "name": "func"
                    }, {
                        "name": "string"
                    }]
                },
                "required": false,
                "description": "The tag type.",
                "defaultValue": {
                    "value": "\"h3\"",
                    "computed": false
                }
            }
        }
    }],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Popovers are powerful elements similar to tooltips and powered by Popper.js that can be applies to any interactive element.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "target": {
            "type": {
                "name": "custom",
                "raw": "CustomPropTypes.target.isRequired"
            },
            "required": false,
            "description": "The target element."
        },
        "container": {
            "type": {
                "name": "custom",
                "raw": "CustomPropTypes.target"
            },
            "required": false,
            "description": "The popover container."
        },
        "modifiers": {
            "type": {
                "name": "object"
            },
            "required": false,
            "description": "Popper modifiers object."
        },
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the popover is open, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "innerClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The inner class name."
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the popover is disabled, or not."
        },
        "noArrow": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether to hide the arrow, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "arrowClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The arrow class name."
        },
        "boundariesElement": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "element"
                }]
            },
            "required": false,
            "description": "The boundaries element for the Popover instance."
        },
        "placement": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The popover placement.",
            "defaultValue": {
                "value": "\"top\"",
                "computed": false
            }
        },
        "placementPrefix": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The placement prefix.",
            "defaultValue": {
                "value": "\"bs-popover\"",
                "computed": false
            }
        },
        "offset": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "number"
                }]
            },
            "required": false,
            "description": "The popover offset."
        },
        "toggle": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The toggle function.",
            "defaultValue": {
                "value": "function() {}",
                "computed": false
            }
        },
        "delay": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "number"
                }, {
                    "name": "shape",
                    "value": {
                        "show": {
                            "name": "number",
                            "required": false
                        },
                        "hide": {
                            "name": "number",
                            "required": false
                        }
                    }
                }]
            },
            "required": false,
            "description": "The show/hide delay in ms.",
            "defaultValue": {
                "value": "{\n  show: 0,\n  hide: 0\n}",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/progress",
    "name": "Progress",
    "filename": "Progress.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport toNumber from \"lodash.tonumber\";\n\n/**\n * You can use the `Progress` component to display simple or complex progress bars.\n */\nconst Progress = props => {\n  const {\n    children,\n    className,\n    barClassName,\n    value,\n    max,\n    animated,\n    striped,\n    theme,\n    bar,\n    multi,\n    tag: Tag,\n    ...attrs\n  } = props;\n\n  const percent = (toNumber(value) / toNumber(max)) * 100;\n  const progressClasses = classNames(className, \"progress\");\n  const progressBarClasses = classNames(\n    \"progress-bar\",\n    bar ? className || barClassName : barClassName,\n    animated && \"progress-bar-animated\",\n    theme && `bg-${theme}`,\n    (striped || animated) && \"progress-bar-striped\"\n  );\n\n  const ProgressBar = multi ? (\n    children\n  ) : (\n    <div\n      className={progressBarClasses}\n      style={{ width: `${percent}%` }}\n      role=\"progressbar\"\n      aria-valuenow={value}\n      aria-valuemin=\"0\"\n      aria-valuemax={max}\n    >\n      {children}\n    </div>\n  );\n\n  if (bar) {\n    return ProgressBar;\n  }\n\n  return (\n    <Tag {...attrs} className={progressClasses}>\n      {ProgressBar}\n    </Tag>\n  );\n};\n\nProgress.propTypes = {\n  /**\n   * The children nodes.\n   */\n  children: PropTypes.node,\n  /**\n   * Whether it is a bar, or not.\n   */\n  bar: PropTypes.bool,\n  /**\n   * Whether there are multiple progress bars nested, or not.\n   */\n  multi: PropTypes.bool,\n  /**\n   * The component's tag type.\n   */\n  tag: PropTypes.string,\n  /**\n   * Whether it is animated, or not.\n   */\n  animated: PropTypes.bool,\n  /**\n   * Whether it is striped, or not.\n   */\n  striped: PropTypes.bool,\n  /**\n   * The theme color.\n   */\n  theme: PropTypes.string,\n  /**\n   * The class name.\n   */\n  className: PropTypes.string,\n  /**\n   * The class name for the bar element.\n   */\n  barClassName: PropTypes.string,\n  /**\n   * The value.\n   */\n  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),\n  /**\n   * The max value.\n   */\n  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number])\n};\n\nProgress.defaultProps = {\n  tag: \"div\",\n  value: 0,\n  max: 100,\n  theme: \"primary\"\n};\n\nexport default Progress;\n",
    "examples": [{
        "name": "Progress",
        "filename": "01-basic-progress.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>You can change the bar's color using the <code>theme</code> prop. The default value is <code>primary</code>.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Progress } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * You can change the bar's color using the `theme` prop. The default value is `primary`.\n */\nexport default function BasicProgress() {\n  return <Progress theme=\"primary\" value={50} />;\n}\n"
    }, {
        "name": "Progress",
        "filename": "02-progress-labels.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Labels</h2>\n<p>You can add labels to your progress bars by providing the value inside of them.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Progress } from \"shards-react\";\n\n/**\n * ## Labels\n *\n * You can add labels to your progress bars by providing the value inside of them.\n */\nexport default function BasicProgress() {\n  return <Progress value=\"20\">20</Progress>;\n}\n"
    }, {
        "name": "Progress",
        "filename": "03-multiple-progress-bars.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Multiple</h2>\n<p>Inside the <code>Progress</code> component you can also nest multiple sub-components.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Progress } from \"shards-react\";\n\n/**\n * ## Multiple\n *\n *Inside the `Progress` component you can also nest multiple sub-components.\n */\nexport default function BasicProgress() {\n  return (\n    <Progress multi>\n      <Progress bar value=\"50\" />\n      <Progress bar theme=\"success\" value=\"20\" />\n    </Progress>\n  );\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>You can use the <code>Progress</code> component to display simple or complex progress bars.</p>\n"
    },
    "props": {
        "children": {
            "type": {
                "name": "node"
            },
            "required": false,
            "description": "The children nodes."
        },
        "bar": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is a bar, or not."
        },
        "multi": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether there are multiple progress bars nested, or not."
        },
        "tag": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The component's tag type.",
            "defaultValue": {
                "value": "\"div\"",
                "computed": false
            }
        },
        "animated": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is animated, or not."
        },
        "striped": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether it is striped, or not."
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The theme color.",
            "defaultValue": {
                "value": "\"primary\"",
                "computed": false
            }
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name."
        },
        "barClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The class name for the bar element."
        },
        "value": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "number"
                }]
            },
            "required": false,
            "description": "The value.",
            "defaultValue": {
                "value": "0",
                "computed": false
            }
        },
        "max": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "number"
                }]
            },
            "required": false,
            "description": "The max value.",
            "defaultValue": {
                "value": "100",
                "computed": false
            }
        }
    }
}, {
    "slug": "component/slider",
    "name": "Slider",
    "filename": "Slider.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport nouislider from \"nouislider\";\n\n/**\n * The slider component is powered behind the scenes by the [NoUiSlider](https://refreshless.com/nouislider/) library.\n */\nclass Slider extends React.Component {\n  componentDidMount() {\n    if (this.props.disabled) {\n      this.sliderContainer.setAttribute(\"disabled\", true);\n    } else {\n      this.sliderContainer.removeAttribute(\"disabled\");\n    }\n\n    this.createSlider();\n  }\n\n  componentDidUpdate() {\n    if (this.props.disabled) {\n      this.sliderContainer.setAttribute(\"disabled\", true);\n    } else {\n      this.sliderContainer.removeAttribute(\"disabled\");\n    }\n\n    this.slider.destroy();\n    this.createSlider();\n  }\n\n  componentWillUnmount() {\n    this.slider.destroy();\n  }\n\n  createSlider() {\n    var slider = (this.slider = nouislider.create(this.sliderContainer, {\n      ...this.props\n    }));\n\n    if (this.props.onUpdate) {\n      slider.on(\"update\", this.props.onUpdate);\n    }\n\n    if (this.props.onChange) {\n      slider.on(\"change\", this.props.onChange);\n    }\n\n    if (this.props.onSlide) {\n      slider.on(\"slide\", this.props.onSlide);\n    }\n\n    if (this.props.onStart) {\n      slider.on(\"start\", this.props.onStart);\n    }\n\n    if (this.props.onEnd) {\n      slider.on(\"end\", this.props.onEnd);\n    }\n\n    if (this.props.onSet) {\n      slider.on(\"set\", this.props.onSet);\n    }\n  }\n\n  render() {\n    const { className, theme } = this.props;\n    const classes = classNames(className, theme && `slider-${theme}`);\n\n    return (\n      <div\n        className={classes}\n        ref={slider => {\n          this.sliderContainer = slider;\n        }}\n      />\n    );\n  }\n}\n\nSlider.propTypes = {\n  className: PropTypes.string,\n  theme: PropTypes.string,\n  animate: PropTypes.bool,\n  behaviour: PropTypes.string,\n  cssPrefix: PropTypes.string,\n  disabled: PropTypes.bool,\n  limit: PropTypes.number,\n  margin: PropTypes.number,\n  onChange: PropTypes.func,\n  onEnd: PropTypes.func,\n  onSet: PropTypes.func,\n  onSlide: PropTypes.func,\n  onStart: PropTypes.func,\n  onUpdate: PropTypes.func,\n  pips: PropTypes.object,\n  range: PropTypes.object.isRequired,\n  start: PropTypes.arrayOf(PropTypes.number).isRequired,\n  step: PropTypes.number,\n  direction: PropTypes.oneOf([\"ltr\", \"rtl\"]),\n  orientation: PropTypes.oneOf([\"horizontal\", \"vertical\"]),\n  connect: PropTypes.oneOfType([\n    PropTypes.arrayOf(PropTypes.bool),\n    PropTypes.bool\n  ]),\n  tooltips: PropTypes.oneOfType([\n    PropTypes.bool,\n    PropTypes.arrayOf(\n      PropTypes.shape({\n        to: PropTypes.func\n      })\n    )\n  ])\n};\n\nexport default Slider;\n",
    "examples": [{
        "name": "Slider",
        "filename": "01-basic-slider.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Slider</h2>\n<p>You can create basic sliders using the <code>Slider</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Basic Slider\n *\n * You can create basic sliders using the `Slider` component.\n */\nexport default function BasicSliderExample() {\n  return (\n    <Slider connect={[true, false]} start={[20]} range={{ min: 0, max: 100 }} />\n  );\n}\n"
    }, {
        "name": "Slider",
        "filename": "02-slider-custom-range.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Custom Range</h2>\n<p>You can provide a custom range for the slider using the <code>range</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Custom Range\n *\n * You can provide a custom range for the slider using the `range` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: 1300 };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: parseFloat(e[0])\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {this.state.value}</p>\n        <Slider\n          onSlide={this.handleSlide}\n          connect={[true, false]}\n          start={[this.state.value]}\n          range={{ min: 0, max: 2500 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Slider",
        "filename": "02-slider-themes.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Slider Themes</h2>\n<p>Adjusting the theme color of the slider can be achieved via the <code>theme</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Slider Themes\n *\n * Adjusting the theme color of the slider can be achieved via the `theme` prop.\n */\nexport default function BasicSliderExample() {\n  return (\n    <Slider\n      theme=\"success\"\n      connect={[true, false]}\n      start={[50]}\n      range={{ min: 0, max: 100 }}\n    />\n  );\n}\n"
    }, {
        "name": "Slider",
        "filename": "03-slider-custom-range.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Custom Range</h2>\n<p>You can provide a custom range for the slider using the <code>range</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Custom Range\n *\n * You can provide a custom range for the slider using the `range` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: 1300 };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: parseFloat(e[0])\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {this.state.value}</p>\n        <Slider\n          onSlide={this.handleSlide}\n          connect={[true, false]}\n          start={[this.state.value]}\n          range={{ min: 0, max: 2500 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Slider",
        "filename": "03-slider-multiple-values.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Multiple Values</h2>\n<p>If you'd like to control multiple values, you can use an <code>Array</code> for the <code>start</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Multiple Values\n *\n * If you'd like to control multiple values, you can use an `Array` for the `start` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: [20, 80] };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: [parseFloat(e[0]), parseFloat(e[1])]\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {JSON.stringify(this.state.value)}</p>\n        <Slider\n          connect\n          onSlide={this.handleSlide}\n          start={this.state.value}\n          range={{ min: 0, max: 100 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Slider",
        "filename": "04-slider-multiple-values.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Multiple Values</h2>\n<p>If you'd like to control multiple values, you can use an <code>Array</code> for the <code>start</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Multiple Values\n *\n * If you'd like to control multiple values, you can use an `Array` for the `start` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: [20, 80] };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: [parseFloat(e[0]), parseFloat(e[1])]\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {JSON.stringify(this.state.value)}</p>\n        <Slider\n          connect\n          onSlide={this.handleSlide}\n          start={this.state.value}\n          range={{ min: 0, max: 100 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Slider",
        "filename": "04-slider-using-pips.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Using Pips</h2>\n<p>Pips can also be enabled using the <code>pips</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Using Pips\n *\n * Pips can also be enabled using the `pips` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: [20, 80] };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: [parseFloat(e[0]), parseFloat(e[1])]\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {JSON.stringify(this.state.value)}</p>\n        <Slider\n          connect\n          pips={{ mode: \"steps\", stepped: true, density: 3 }}\n          onSlide={this.handleSlide}\n          start={this.state.value}\n          range={{ min: 0, max: 100 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Slider",
        "filename": "05-slider-using-pips.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Using Pips</h2>\n<p>Pips can also be enabled using the <code>pips</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Slider } from \"shards-react\";\n\n/**\n * ## Using Pips\n *\n * Pips can also be enabled using the `pips` prop.\n */\nexport default class SliderCustomRange extends React.Component {\n  constructor(props) {\n    super(props);\n    this.handleSlide = this.handleSlide.bind(this);\n    this.state = { value: [20, 80] };\n  }\n\n  handleSlide(e) {\n    this.setState({\n      value: [parseFloat(e[0]), parseFloat(e[1])]\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <p>Value: {JSON.stringify(this.state.value)}</p>\n        <Slider\n          connect\n          pips={{ mode: \"steps\", stepped: true, density: 3 }}\n          onSlide={this.handleSlide}\n          start={this.state.value}\n          range={{ min: 0, max: 100 }}\n        />\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>The slider component is powered behind the scenes by the <a href=\"https://refreshless.com/nouislider/\">NoUiSlider</a> library.</p>\n"
    },
    "props": {
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": ""
        },
        "theme": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": ""
        },
        "animate": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": ""
        },
        "behaviour": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": ""
        },
        "cssPrefix": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": ""
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": ""
        },
        "limit": {
            "type": {
                "name": "number"
            },
            "required": false,
            "description": ""
        },
        "margin": {
            "type": {
                "name": "number"
            },
            "required": false,
            "description": ""
        },
        "onChange": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "onEnd": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "onSet": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "onSlide": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "onStart": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "onUpdate": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": ""
        },
        "pips": {
            "type": {
                "name": "object"
            },
            "required": false,
            "description": ""
        },
        "range": {
            "type": {
                "name": "object"
            },
            "required": true,
            "description": ""
        },
        "start": {
            "type": {
                "name": "arrayOf",
                "value": {
                    "name": "number"
                }
            },
            "required": true,
            "description": ""
        },
        "step": {
            "type": {
                "name": "number"
            },
            "required": false,
            "description": ""
        },
        "direction": {
            "type": {
                "name": "enum",
                "value": [{
                    "value": "\"ltr\"",
                    "computed": false
                }, {
                    "value": "\"rtl\"",
                    "computed": false
                }]
            },
            "required": false,
            "description": ""
        },
        "orientation": {
            "type": {
                "name": "enum",
                "value": [{
                    "value": "\"horizontal\"",
                    "computed": false
                }, {
                    "value": "\"vertical\"",
                    "computed": false
                }]
            },
            "required": false,
            "description": ""
        },
        "connect": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "arrayOf",
                    "value": {
                        "name": "bool"
                    }
                }, {
                    "name": "bool"
                }]
            },
            "required": false,
            "description": ""
        },
        "tooltips": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "bool"
                }, {
                    "name": "arrayOf",
                    "value": {
                        "name": "shape",
                        "value": {
                            "to": {
                                "name": "func",
                                "required": false
                            }
                        }
                    }
                }]
            },
            "required": false,
            "description": ""
        }
    }
}, {
    "slug": "component/tooltip",
    "name": "Tooltip",
    "filename": "Tooltip.js",
    "code": "import React from \"react\";\nimport PropTypes from \"prop-types\";\nimport classNames from \"classnames\";\nimport omit from \"lodash.omit\";\n\nimport { getTarget, CustomPropTypes } from \"../utils\";\nimport { TIMEOUT, EVENTS, POPPER_PLACEMENTS } from \"../constants\";\nimport PopperManager from \"../../utils/PopperManager\";\n\n/**\n * Tooltips are powerful components powered behind the scenes by Popper.js that can be attached to any element.\n */\nclass Tooltip extends React.Component {\n  constructor(props) {\n    super(props);\n\n    this.addListeners = this.addListeners.bind(this);\n    this.removeListeners = this.removeListeners.bind(this);\n    this.handleClick = this.handleClick.bind(this);\n    this.handleMouseEnter = this.handleMouseEnter.bind(this);\n    this.handleMouseLeave = this.handleMouseLeave.bind(this);\n    this.handleMouseOverContent = this.handleMouseOverContent.bind(this);\n    this.handleMouseLeaveContent = this.handleMouseLeaveContent.bind(this);\n    this.show = this.show.bind(this);\n    this.hide = this.hide.bind(this);\n    this.toggle = this.toggle.bind(this);\n\n    this._target = null;\n    this._hideTimeout = null;\n    this._showTimeout = null;\n  }\n\n  componentDidMount() {\n    this._target = getTarget(this.props.target);\n    this.addListeners();\n  }\n\n  componentWillUnmount() {\n    clearTimeout(this._hideTimeout);\n    clearTimeout(this._showTimeout);\n    this.removeListeners();\n  }\n\n  addListeners() {\n    const triggers = this.props.trigger.trim().split(/\\s+/);\n\n    triggers.forEach(trigger => {\n      switch (trigger) {\n        case \"click\":\n          EVENTS.CLICK.forEach(e => document.addEventListener(e, this));\n          break;\n\n        case \"hover\":\n          EVENTS.MOUSE.forEach(e => this._target.addEventListener(e, this));\n          break;\n\n        case \"focus\":\n          EVENTS.FOCUS.forEach(e => this._target.addEventListener(e, this));\n          break;\n\n        default:\n          break;\n      }\n    }, this);\n  }\n\n  removeListeners() {\n    EVENTS.CLICK.forEach(e => document.removeEventListener(e, this), this);\n    EVENTS.MOUSE.concat(EVENTS.FOCUS).forEach(\n      e => this._target.removeEventListener(e, this),\n      this\n    );\n  }\n\n  handleEvent(e) {\n    if (this.props.disabled || this._target === null) {\n      return;\n    }\n\n    switch (e.type) {\n      case \"click\":\n      case \"touchstart\":\n        this.handleClick(e);\n        break;\n\n      case \"mouseenter\":\n        this.handleMouseEnter(e);\n        break;\n\n      case \"mouseleave\":\n        this.handleMouseLeave(e);\n        break;\n\n      case \"focusin\":\n        this.show(e);\n        break;\n\n      case \"focusout\":\n        this.hide(e);\n        break;\n\n      default:\n        break;\n    }\n  }\n\n  handleClick(e) {\n    if (\n      this._target !== null &&\n      (e.target === this._target || this._target.contains(e.target))\n    ) {\n      if (this._hideTimeout) {\n        clearTimeout(this._hideTimeout);\n      }\n\n      if (!this.props.open) {\n        this.toggle(e);\n      }\n\n      return;\n    }\n\n    if (this.props.open && e.target.getAttribute(\"role\") !== \"tooltip\") {\n      if (this._showTimeout) {\n        clearTimeout(this._showTimeout);\n      }\n\n      this._hideTimeout = setTimeout(\n        this.hide.bind(this, e),\n        this.getDelay(\"hide\")\n      );\n    }\n  }\n\n  handleMouseEnter(e) {\n    if (this._hideTimeout) {\n      clearTimeout(this._hideTimeout);\n    }\n\n    this._showTimeout = setTimeout(\n      this.show.bind(this, e),\n      this.getDelay(\"show\")\n    );\n  }\n\n  handleMouseLeave(e) {\n    if (this._showTimeout) {\n      clearTimeout(this._showTimeout);\n    }\n\n    this._hideTimeout = setTimeout(\n      this.hide.bind(this, e),\n      this.getDelay(\"hide\")\n    );\n  }\n\n  handleMouseOverContent() {\n    if (this.props.autohide) {\n      return;\n    }\n\n    if (this._hideTimeout) {\n      clearTimeout(this._hideTimeout);\n    }\n  }\n\n  handleMouseLeaveContent(e) {\n    if (this.props.autohide) {\n      return;\n    }\n\n    if (this._showTimeout) {\n      clearTimeout(this._showTimeout);\n    }\n\n    e.persist();\n    this._hideTimeout = setTimeout(\n      this.hide.bind(this, e),\n      this.getDelay(\"hide\")\n    );\n  }\n\n  getDelay(key) {\n    key = key.toUpperCase();\n    if (typeof this.props.delay === \"object\") {\n      return isNaN(this.props.delay[key])\n        ? TIMEOUT[key]\n        : this.props.delay[key];\n    }\n\n    return this.props.delay;\n  }\n\n  show(e) {\n    if (!this.props.open) {\n      clearTimeout(this._showTimeout);\n      this.toggle(e);\n    }\n  }\n\n  hide(e) {\n    if (this.props.open) {\n      clearTimeout(this._hideTimeout);\n      this.toggle(e);\n    }\n  }\n\n  toggle(e) {\n    if (this.props.disabled) {\n      return e && e.preventDefault();\n    }\n\n    return this.props.toggle(e);\n  }\n\n  render() {\n    const _props = omit(this.props, [\n      \"trigger\",\n      \"disabled\",\n      \"delay\",\n      \"toggle\",\n      \"autohide\"\n    ]);\n    const {\n      target,\n      container,\n      open,\n      className,\n      arrowClassName,\n      innerClassName,\n      boundariesElement,\n      placement,\n      placementPrefix,\n      modifiers,\n      offset,\n      noArrow,\n      ...attrs\n    } = _props;\n\n    if (!open) {\n      return null;\n    }\n\n    const classes = classNames(\"tooltip-inner\", innerClassName);\n\n    const popperClasses = classNames(\"tooltip\", \"show\", className);\n\n    return (\n      <PopperManager\n        container={container}\n        className={popperClasses}\n        arrowClassName={arrowClassName}\n        target={target}\n        open={open}\n        noArrow={noArrow}\n        boundariesElement={boundariesElement}\n        placement={placement}\n        placementPrefix={placementPrefix}\n        modifiers={modifiers}\n        offset={offset}\n      >\n        <div\n          {...attrs}\n          className={classes}\n          role=\"tooltip\"\n          aria-hidden={open}\n          onMouseOver={this.handleMouseOverContent}\n          onMouseLeave={this.handleMouseLeaveContent}\n        />\n      </PopperManager>\n    );\n  }\n}\n\nTooltip.propTypes = {\n  /**\n   * The target element.\n   */\n  target: CustomPropTypes.target.isRequired,\n\n  /**\n   * The tooltip container.\n   */\n  container: CustomPropTypes.target,\n\n  /**\n   * The trigger(s) (click, hover, focus).\n   */\n  trigger: PropTypes.string,\n\n  /**\n   * Whether the tooltip is open, or not.\n   */\n  open: PropTypes.bool,\n\n  /**\n   * Whether the tooltip is disabled, or not.\n   */\n  disabled: PropTypes.bool,\n\n  /**\n   * The tooltip class name.\n   */\n  className: PropTypes.string,\n\n  /**\n   * The arrow class name.\n   */\n  arrowClassName: PropTypes.string,\n\n  /**\n   * The tooltip inner class name.\n   */\n  innerClassName: PropTypes.string,\n\n  /**\n   * The tooltip offset.\n   */\n  offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),\n\n  /**\n   * The show/hide delay in ms.\n   */\n  delay: PropTypes.oneOfType([\n    PropTypes.number,\n    PropTypes.shape({\n      show: PropTypes.number,\n      hide: PropTypes.number\n    })\n  ]),\n\n  /**\n   * The boundaries element for the tooltip instance.\n   */\n  boundariesElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),\n\n  /**\n   * The tooltip placement.\n   */\n  placement: PropTypes.oneOf(POPPER_PLACEMENTS),\n\n  /**\n   * The placement prefix.\n   */\n  placementPrefix: PropTypes.string,\n\n  /**\n   * Whether to hide the arrow, or not.\n   */\n  noArrow: PropTypes.bool,\n\n  /**\n   * The toggle function.\n   */\n  toggle: PropTypes.func.isRequired,\n\n  /**\n   * Popper modifiers object.\n   */\n  modifiers: PropTypes.object,\n\n  /**\n   * Whether the tooltip should auto-hide, or not.\n   */\n  autohide: PropTypes.bool\n};\n\nTooltip.defaultProps = {\n  trigger: \"hover\",\n  open: false,\n  disabled: false,\n  noArrow: false,\n  placement: \"top\",\n  placementPrefix: \"bs-tooltip\",\n  autohide: true,\n  delay: {\n    show: 0,\n    hide: 0\n  },\n  toggle: function() {}\n};\n\nexport default Tooltip;\n",
    "examples": [{
        "name": "Tooltip",
        "filename": "01-basic-tooltip.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Basic Example</h2>\n<p>Tooltips can be created using the <code>Tooltip</code> component.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Tooltip, Button } from \"shards-react\";\n\n/**\n * ## Basic Example\n *\n * Tooltips can be created using the `Tooltip` component.\n */\nexport default class TooltipExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState({\n      open: !this.state.open\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button id=\"TooltipExample\">Hover Me!</Button>\n        <Tooltip\n          open={this.state.open}\n          target=\"#TooltipExample\"\n          toggle={this.toggle}\n        >\n          😁 Woo! I am a tooltip!\n        </Tooltip>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Tooltip",
        "filename": "02-tooltip-placement.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Placement</h2>\n<p>By default, the tooltips are placed on top. However, you can change this via the <code>placement</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Tooltip, Button } from \"shards-react\";\n\n/**\n * ## Placement\n *\n * By default, the tooltips are placed on top. However, you can change this via the `placement` prop.\n */\nexport default class TooltipPlacementExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = {\n      top: false,\n      left: false,\n      bottom: false,\n      right: false\n    };\n  }\n\n  toggle(pos) {\n    const newState = {};\n    newState[pos] = !this.state[pos];\n    this.setState({ ...this.state, ...newState });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button id=\"TooltipTop\" className=\"mr-2\">\n          Top\n        </Button>\n        <Tooltip\n          open={this.state.top}\n          target=\"#TooltipTop\"\n          toggle={() => this.toggle(\"top\")}\n        >\n          I am on top! ☝️\n        </Tooltip>\n\n        <Button id=\"TooltipBottom\" className=\"mr-2\">\n          Bottom\n        </Button>\n        <Tooltip\n          placement=\"bottom\"\n          open={this.state.bottom}\n          target=\"#TooltipBottom\"\n          toggle={() => this.toggle(\"bottom\")}\n        >\n          I am at the bottom! 👇\n        </Tooltip>\n\n        <Button id=\"TooltipLeft\" className=\"mr-2\">\n          Left\n        </Button>\n        <Tooltip\n          placement=\"left\"\n          open={this.state.left}\n          target=\"#TooltipLeft\"\n          toggle={() => this.toggle(\"left\")}\n        >\n          I am on the left! 👈\n        </Tooltip>\n\n        <Button id=\"TooltipRight\" className=\"mr-2\">\n          Right\n        </Button>\n        <Tooltip\n          placement=\"right\"\n          open={this.state.right}\n          target=\"#TooltipRight\"\n          toggle={() => this.toggle(\"right\")}\n        >\n          I am on the right! 👉\n        </Tooltip>\n      </div>\n    );\n  }\n}\n"
    }, {
        "name": "Tooltip",
        "filename": "03-tooltip-trigger.js",
        "description": {
            "data": {},
            "messages": [],
            "history": [],
            "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
            "contents": "<h2>Triggers</h2>\n<p>You can control what triggers your tooltip to show using the <code>trigger</code> prop.</p>\n"
        },
        "code": "import React from \"react\";\nimport { Tooltip, Button } from \"shards-react\";\n\n/**\n * ## Triggers\n *\n * You can control what triggers your tooltip to show using the `trigger` prop.\n */\nexport default class TooltipClickExample extends React.Component {\n  constructor(props) {\n    super(props);\n    this.toggle = this.toggle.bind(this);\n    this.state = { open: false };\n  }\n\n  toggle() {\n    this.setState({\n      open: !this.state.open\n    });\n  }\n\n  render() {\n    return (\n      <div>\n        <Button id=\"TooltipClickExample\">Click Me!</Button>\n        <Tooltip\n          trigger=\"click\"\n          open={this.state.open}\n          target=\"#TooltipClickExample\"\n          toggle={this.toggle}\n        >\n          😁 You clicked to see me!\n        </Tooltip>\n      </div>\n    );\n  }\n}\n"
    }],
    "subcomponents": [],
    "description": {
        "data": {},
        "messages": [],
        "history": [],
        "cwd": "/Users/hisk/Projects/GitHub/shards-react-main",
        "contents": "<p>Tooltips are powerful components powered behind the scenes by Popper.js that can be attached to any element.</p>\n"
    },
    "props": {
        "target": {
            "type": {
                "name": "custom",
                "raw": "CustomPropTypes.target.isRequired"
            },
            "required": false,
            "description": "The target element."
        },
        "container": {
            "type": {
                "name": "custom",
                "raw": "CustomPropTypes.target"
            },
            "required": false,
            "description": "The tooltip container."
        },
        "trigger": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The trigger(s) (click, hover, focus).",
            "defaultValue": {
                "value": "\"hover\"",
                "computed": false
            }
        },
        "open": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the tooltip is open, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "disabled": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the tooltip is disabled, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "className": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The tooltip class name."
        },
        "arrowClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The arrow class name."
        },
        "innerClassName": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The tooltip inner class name."
        },
        "offset": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "number"
                }]
            },
            "required": false,
            "description": "The tooltip offset."
        },
        "delay": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "number"
                }, {
                    "name": "shape",
                    "value": {
                        "show": {
                            "name": "number",
                            "required": false
                        },
                        "hide": {
                            "name": "number",
                            "required": false
                        }
                    }
                }]
            },
            "required": false,
            "description": "The show/hide delay in ms.",
            "defaultValue": {
                "value": "{\n  show: 0,\n  hide: 0\n}",
                "computed": false
            }
        },
        "boundariesElement": {
            "type": {
                "name": "union",
                "value": [{
                    "name": "string"
                }, {
                    "name": "element"
                }]
            },
            "required": false,
            "description": "The boundaries element for the tooltip instance."
        },
        "placement": {
            "type": {
                "name": "enum",
                "computed": true,
                "value": "POPPER_PLACEMENTS"
            },
            "required": false,
            "description": "The tooltip placement.",
            "defaultValue": {
                "value": "\"top\"",
                "computed": false
            }
        },
        "placementPrefix": {
            "type": {
                "name": "string"
            },
            "required": false,
            "description": "The placement prefix.",
            "defaultValue": {
                "value": "\"bs-tooltip\"",
                "computed": false
            }
        },
        "noArrow": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether to hide the arrow, or not.",
            "defaultValue": {
                "value": "false",
                "computed": false
            }
        },
        "toggle": {
            "type": {
                "name": "func"
            },
            "required": false,
            "description": "The toggle function.",
            "defaultValue": {
                "value": "function() {}",
                "computed": false
            }
        },
        "modifiers": {
            "type": {
                "name": "object"
            },
            "required": false,
            "description": "Popper modifiers object."
        },
        "autohide": {
            "type": {
                "name": "bool"
            },
            "required": false,
            "description": "Whether the tooltip should auto-hide, or not.",
            "defaultValue": {
                "value": "true",
                "computed": false
            }
        }
    }
}]